export type SmartSearchGuidedLanguageCode =
  | 'en'
  | 'zh-CN'
  | 'zh-TW'
  | 'es'
  | 'ko'
  | 'ja'
  | 'vi'
  | 'fr'
  | 'ar-SA';

export type GuidedTextDirection = 'ltr' | 'rtl';

type GuidedIdentityKey = 'work' | 'student' | 'other';
type GuidedLocationModeKey = 'commute' | 'live' | 'explore';
type GuidedCommuteOptionKey = 'skip' | 'target';
type GuidedRecommendationStyleKey = 'value' | 'commute' | 'balanced';
type GuidedBedroomModeKey = 'at_least' | 'exact';
type GuidedQuestionKey =
  | 'identity'
  | 'schoolName'
  | 'locationMode'
  | 'location'
  | 'commuteOption'
  | 'commuteDestination'
  | 'commuteMethod'
  | 'maxCommuteTime'
  | 'propertyType'
  | 'budget'
  | 'moveInDate'
  | 'bedrooms'
  | 'bathrooms'
  | 'parking'
  | 'pets'
  | 'mustHaveInUnit'
  | 'mustHaveAmenities'
  | 'recommendationStyle';

interface GuidedCardCopy {
  label: string;
  description: string;
}

interface GuidedRecommendationStyleCopy {
  optionLabel: string;
  title: string;
  description: string;
}

interface GuidedTutorialCopy {
  eyebrow: string;
  title: string;
  body: string;
  dismiss: string;
}

export interface SmartSearchGuidedCopy {
  languageName: string;
  textDirection: GuidedTextDirection;
  identityIntro: string;
  smartProfileGuidedUpdateIntro: string;
  returningProfileGreeting: string;
  sidebarTutorial: GuidedTutorialCopy & {
    savedProfile: GuidedTutorialCopy;
    currentSearch: GuidedTutorialCopy;
  };
  languageSelectorTutorial: GuidedTutorialCopy;
  mapToggleTutorial: GuidedTutorialCopy;
  mapResizeTutorial: GuidedTutorialCopy;
  mapCommuteModeTutorial: GuidedTutorialCopy;
  questions: Record<GuidedQuestionKey, string>;
  questionPlaceholders: Partial<Record<GuidedQuestionKey, string>>;
  identityLabels: Record<GuidedIdentityKey, string>;
  identityChatMessages: Record<GuidedIdentityKey, string>;
  identityCards: Record<GuidedIdentityKey, GuidedCardCopy>;
  locationModeLabels: Record<GuidedLocationModeKey, string>;
  locationModeDescriptions: Record<GuidedLocationModeKey, string>;
  locationQuestions: {
    studentCommuteTarget: (schoolName: string) => string;
    studentCampusTarget: (schoolName: string) => string;
    workCommuteTarget: string;
    otherCommuteTarget: string;
    dailyDestination: string;
    confirmBeforeLivingAreas: string;
    addLivingAreas: string;
    confirmBeforeBroadSearch: string;
    addStarterLivingAreas: string;
  };
  commuteModeDescription: {
    withTarget: (target: string) => string;
    withoutTarget: string;
  };
  postResultCommuteQuestion: string;
  postResultCommuteAnswerLabels: {
    yes: string;
    no: string;
  };
  commuteOptionLabels: Record<GuidedCommuteOptionKey, string>;
  commuteOptionDescriptions: Record<GuidedCommuteOptionKey, string>;
  commuteMethodLabels: Record<string, string>;
  commuteTimeLabels: Record<string, string>;
  propertyTypeLabels: Record<string, string>;
  propertyTypeNoPreference: string;
  budgetLabels: {
    others: string;
    min: string;
    max: string;
    apply: string;
  };
  moveInDateLabels: {
    title: string;
    selectDate: string;
    rangeSeparator: string;
    earliestAriaLabel: string;
    latestAriaLabel: string;
    asap: string;
    asapAriaLabel: string;
  };
  bedroomModeLabels: Record<GuidedBedroomModeKey, string>;
  bedroomLabels: Record<string, string>;
  bathroomLabels: Record<string, string>;
  parkingAnswerLabels: {
    yes: string;
    no: string;
  };
  petAnswerLabels: {
    yes: string;
    no: string;
  };
  recommendationStyles: Record<GuidedRecommendationStyleKey, GuidedRecommendationStyleCopy>;
  recommendationStyleDisabledReasons: {
    commuteNoLimit: string;
    commuteMissingTarget: string;
    balancedComingSoon: string;
  };
  schoolStep: {
    supportedQuestion: string;
    supportedHint: string;
    unsupportedSearchButton: string;
    backToSchoolList: string;
    checkingCampusBuildings: string;
    noCampusBuildingsTitle: string;
    noCampusBuildingsBody: string;
    searchExactPlace: string;
    unsupportedBuildingSearchButton: string;
    backToCampusBuildings: string;
  };
  livingAreaStep: {
    popularAreaQuickPicks: string;
    addSupportedArea: string;
    searchExactPlace: string;
    unsupportedAreaSearchButton: string;
    backToSupportedAreas: string;
  };
  priorCommuteStep: {
    explorePrompt: string;
    livePrompt: string;
    edit: string;
    cancel: string;
    enterAnotherLocation: string;
    skipCommute: string;
    confirm: string;
  };
  ui: {
    continue: string;
    skip: string;
    noSpecificWorkAddress: string;
    confirm: string;
    confirmTarget: string;
    confirmFilter: string;
    exclusionProfileSaveNote: string;
    noMatch: string;
    match: string;
    loadingOptions: string;
    noMustHaves: string;
    selectAllThatApply: string;
  };
}

export interface GuidedLanguageOption {
  code: SmartSearchGuidedLanguageCode;
  label: string;
}

export const DEFAULT_GUIDED_LANGUAGE: SmartSearchGuidedLanguageCode = 'en';

export const GUIDED_LANGUAGE_OPTIONS: GuidedLanguageOption[] = [
  { code: 'en', label: 'English' },
  { code: 'zh-CN', label: '简体中文' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'es', label: 'Español' },
  { code: 'ko', label: '한국어' },
  { code: 'ja', label: '日本語' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'fr', label: 'Français' },
  { code: 'ar-SA', label: 'العربية (السعودية)' },
];

export const GUIDED_LANGUAGE_PROMPT =
  'Welcome to Mimi. Choose a language for the guided questions that will set up your personal profile.';

const SUPPORTED_GUIDED_LANGUAGE_CODES = new Set(
  GUIDED_LANGUAGE_OPTIONS.map(option => option.code)
);

const resolveGuidedLanguage = (language?: string | null): SmartSearchGuidedLanguageCode | undefined => {
  const normalized = String(language || '').trim();
  if (!normalized) return undefined;
  if (SUPPORTED_GUIDED_LANGUAGE_CODES.has(normalized as SmartSearchGuidedLanguageCode)) {
    return normalized as SmartSearchGuidedLanguageCode;
  }

  const lower = normalized.toLowerCase();
  if (lower.startsWith('zh-hant') || lower === 'zh-tw' || lower === 'zh-hk' || lower === 'zh-mo') return 'zh-TW';
  if (lower.startsWith('zh')) return 'zh-CN';
  if (lower.startsWith('es')) return 'es';
  if (lower.startsWith('ko')) return 'ko';
  if (lower.startsWith('ja')) return 'ja';
  if (lower.startsWith('vi')) return 'vi';
  if (lower.startsWith('fr')) return 'fr';
  if (lower.startsWith('ar')) return 'ar-SA';
  if (lower.startsWith('en')) return 'en';
  return undefined;
};

export const normalizeGuidedLanguage = (language?: string | null): SmartSearchGuidedLanguageCode =>
  resolveGuidedLanguage(language) || DEFAULT_GUIDED_LANGUAGE;

export const isGuidedLanguageCode = (language?: string | null): language is SmartSearchGuidedLanguageCode =>
  Boolean(resolveGuidedLanguage(language) === language);

export const getPreferredGuidedLanguage = (
  languages?: readonly string[] | string | null
): SmartSearchGuidedLanguageCode => {
  const candidates = Array.isArray(languages)
    ? languages
    : languages
      ? [languages]
      : [];
  for (const candidate of candidates) {
    const resolved = resolveGuidedLanguage(candidate);
    if (resolved) return resolved;
  }
  return DEFAULT_GUIDED_LANGUAGE;
};

const en: SmartSearchGuidedCopy = {
  languageName: 'English',
  textDirection: 'ltr',
  identityIntro: "Hi, I’m Mimi, your AI rental assistant. I’m here to make finding your next place easier. Let’s set up your search around the reason you’re moving, then I’ll narrow the options from there based on your requirements and lifestyle.",
  smartProfileGuidedUpdateIntro: 'Sure thing. Let’s review your saved Smart Search profile and update anything that no longer fits.',
  returningProfileGreeting: 'Welcome back. I loaded your saved Smart Search profile for this chat. Do you want to continue with your current preferences, or update anything before I search?',
  sidebarTutorial: {
    eyebrow: 'Quick tip',
    title: 'Need more room for chat?',
    body: 'Use this icon to collapse or reopen your Smart Search profile panel anytime.',
    dismiss: 'Got it',
    savedProfile: {
      eyebrow: 'Profile panel',
      title: 'Saved profile',
      body: 'This button shows the preferences Mimi has saved for future Smart Search chats.',
      dismiss: 'Got it',
    },
    currentSearch: {
      eyebrow: 'Profile panel',
      title: 'Current search',
      body: 'Use this button to compare or save the criteria from the search you are working on now.',
      dismiss: 'Got it',
    },
  },
  languageSelectorTutorial: {
    eyebrow: 'Quick tip',
    title: 'Choose your language',
    body: 'Use the globe to switch Mimi’s guided questions into the language you prefer.',
    dismiss: 'Got it',
  },
  mapToggleTutorial: {
    eyebrow: 'Quick tip',
    title: 'Open the map',
    body: 'Use this map button to explore your recommended homes on a map.',
    dismiss: 'Got it',
  },
  mapResizeTutorial: {
    eyebrow: 'Tip',
    title: 'Drag to resize',
    body: 'Pull this corner.',
    dismiss: 'Got it',
  },
  mapCommuteModeTutorial: {
    eyebrow: 'Tip',
    title: 'Switch travel mode',
    body: 'Hover a home to see the commute.',
    dismiss: 'Got it',
  },
  questions: {
    identity: 'Which best describes you?',
    schoolName: 'Which school should Mimi use for your search?',
    locationMode: 'How should Mimi set up your location profile?',
    location: 'Tell me the place so I can confirm the right location.',
    commuteOption: 'Would you like to add a commute target before I search?',
    commuteDestination: 'Where would you commute to?',
    commuteMethod: 'How will you usually commute there?',
    maxCommuteTime: 'What is the maximum commute time you would accept?',
    propertyType: 'What property type are you looking for?',
    budget: 'What is your budget? Please specify the price range.',
    moveInDate: 'When would you like to move in?',
    bedrooms: 'How many bedrooms do you want?',
    bathrooms: 'How many bathrooms do you need?',
    parking: 'Do you need parking?',
    pets: 'Do you have pets?',
    mustHaveInUnit: 'Any must-have in-unit features? Pick the ones you can’t live without — or skip.',
    mustHaveAmenities: 'Any must-have building or neighborhood amenities? Pick the ones that matter — or skip.',
    recommendationStyle: 'How should Mimi prioritize your first recommendations?',
  },
  questionPlaceholders: {
    parking: 'e.g., yes, no, nice to have',
    pets: 'e.g., no pets, cat, small dog',
  },
  identityLabels: {
    work: 'Work',
    student: 'School / student',
    other: 'Other',
  },
  identityChatMessages: {
    work: "I'd like to live near work.",
    student: "I'd like to live near school.",
    other: 'I have a different priority in mind.',
  },
  identityCards: {
    work: { label: 'Near Work', description: 'Close to work or weekday routine' },
    student: { label: 'Near School', description: 'Close to school, research, or campus life' },
    other: { label: 'Something Else', description: 'Here for another reason' },
  },
  locationModeLabels: {
    commute: 'Find Nearby Homes',
    live: 'Specific Area in Mind',
    explore: 'Not Sure Yet',
  },
  locationModeDescriptions: {
    commute: 'Control commute time to a daily destination.',
    live: 'I already have a neighborhood or address in mind.',
    explore: 'I’m new to Boston and would like some suggestions.',
  },
  locationQuestions: {
    studentCommuteTarget: schoolName => `Choose your commute target for ${schoolName}. Select a campus building below, or search an exact address or landmark.`,
    studentCampusTarget: schoolName => `For ${schoolName}, choose your campus building below. If it is not listed, search for the exact address or landmark.`,
    workCommuteTarget: 'Which workplace or office should Mimi use as your commute target?',
    otherCommuteTarget: 'Where would you commute to?',
    dailyDestination: 'Which daily destination should Mimi use as your commute target?',
    confirmBeforeLivingAreas: 'Confirm your commute target before choosing living areas.',
    addLivingAreas: 'Add one or more living areas. Choose supported areas or search Google places, then continue.',
    confirmBeforeBroadSearch: 'Confirm your commute target before I search broadly.',
    addStarterLivingAreas: 'Add one or more starter living areas. We can refine after the first results.',
  },
  commuteModeDescription: {
    withTarget: target => `Control commute time to ${target}.`,
    withoutTarget: 'Control commute time to a daily destination.',
  },
  postResultCommuteQuestion: 'Would you like to add a commute destination to your current search?',
  postResultCommuteAnswerLabels: { yes: 'Yes', no: 'No' },
  commuteOptionLabels: {
    skip: 'Skip commute for now',
    target: 'Have a target',
  },
  commuteOptionDescriptions: {
    skip: 'Search without a commute filter.',
    target: 'Pick a separate destination with Google.',
  },
  commuteMethodLabels: {
    driving: 'Driving',
    transit: 'Transit',
    walking: 'Walking',
    bicycling: 'Cycling',
  },
  commuteTimeLabels: {
    '15 minutes': '≤ 15 min',
    '30 minutes': '≤ 30 min',
    '45 minutes': '≤ 45 min',
    '60 minutes': '≤ 60 min',
    '': 'Doesn’t matter',
  },
  propertyTypeLabels: {
    apartment: 'Apartment',
    condo: 'Condo',
    townhouse: 'Townhouse',
    joint_rent: 'Joint rent',
    any: 'Doesn’t matter',
  },
  propertyTypeNoPreference: 'Doesn’t matter',
  budgetLabels: {
    others: 'Others',
    min: 'Min',
    max: 'Max',
    apply: 'Apply',
  },
  moveInDateLabels: {
    title: 'Move in date',
    selectDate: 'Select date',
    rangeSeparator: 'to',
    earliestAriaLabel: 'Select earliest move-in date',
    latestAriaLabel: 'Select latest move-in date',
    asap: 'As soon as possible',
    asapAriaLabel: 'Move in as soon as possible',
  },
  bedroomModeLabels: {
    at_least: 'At least',
    exact: 'Exact',
  },
  bedroomLabels: {
    studio: 'Studio',
    '1': '1 bedroom',
    '2': '2 bedroom',
    '3': '3 bedroom',
  },
  bathroomLabels: {
    '1': '≥ 1 bathroom',
    '2': '≥ 2 bathroom',
    '3': '≥ 3 bathroom',
  },
  parkingAnswerLabels: {
    yes: 'Yes',
    no: 'No',
  },
  petAnswerLabels: {
    yes: 'Yes',
    no: 'No',
  },
  recommendationStyles: {
    value: {
      optionLabel: 'Option A',
      title: 'Value-Focused',
      description: 'Prioritizes the best balance of price and features.',
    },
    commute: {
      optionLabel: 'Option B',
      title: 'Commute-Focused',
      description: 'Prioritizes convenience and shorter travel times.',
    },
    balanced: {
      optionLabel: 'Option C',
      title: 'Best Overall Match',
      description: 'Balances affordability, commute and lifestyle.',
    },
  },
  recommendationStyleDisabledReasons: {
    commuteNoLimit: 'You said commute time doesn’t matter, so Mimi will skip commute-focused ranking for now.',
    commuteMissingTarget: 'Needs a commute target and max time. Mimi will skip this for now.',
    balancedComingSoon: 'Coming soon while we tune the balanced ranking logic.',
  },
  schoolStep: {
    supportedQuestion: 'Where is your college/ university?',
    supportedHint: 'Choose another school from our supported list.',
    unsupportedSearchButton: "Don't see your school? Search here!",
    backToSchoolList: 'Back to school list',
    checkingCampusBuildings: 'Checking campus buildings...',
    noCampusBuildingsTitle: 'No campus buildings found yet',
    noCampusBuildingsBody: 'Search the exact campus address, building name, or nearby landmark.',
    searchExactPlace: 'Search exact place',
    unsupportedBuildingSearchButton: "Don't see the building? Search here!",
    backToCampusBuildings: 'Back to campus buildings',
  },
  livingAreaStep: {
    popularAreaQuickPicks: 'Popular area quick picks',
    addSupportedArea: 'Add another area from our supported list.',
    searchExactPlace: 'Search an exact address, landmark, or place.',
    unsupportedAreaSearchButton: "Don't see your area? Search here!",
    backToSupportedAreas: 'Back to supported area list',
  },
  priorCommuteStep: {
    explorePrompt: 'Use this as your commute destination, edit it, or skip commute before I search broadly.',
    livePrompt: 'Use this as your commute destination, edit it, or skip commute before choosing your living area.',
    edit: 'Edit',
    cancel: 'Cancel',
    enterAnotherLocation: 'Enter another location',
    skipCommute: 'Skip commute',
    confirm: 'Confirm',
  },
  ui: {
    continue: 'Continue',
    skip: 'Skip',
    noSpecificWorkAddress: 'Don’t have a specific address yet?',
    confirm: 'Confirm',
    confirmTarget: 'Confirm target',
    confirmFilter: 'Confirm filter',
    exclusionProfileSaveNote: "Area exclusions apply to this search and aren’t saved to your profile.",
    noMatch: 'No match',
    match: 'Match',
    loadingOptions: 'Loading options…',
    noMustHaves: 'No must-haves',
    selectAllThatApply: 'Select all that apply.',
  },
};

const zhCN: SmartSearchGuidedCopy = {
  ...en,
  languageName: '中文（简体）',
  identityIntro: '你好，我是你的 AI 租房助手 Mimi。我们先根据你的搬家原因建立搜索方向，再结合你的需求和生活方式缩小选择范围。',
  smartProfileGuidedUpdateIntro: '好的。我们来复查你已保存的 Smart Search 资料，并更新任何不再适合的内容。',
  returningProfileGreeting: '欢迎回来。我已为这次聊天加载你保存的 Smart Search 资料。你想继续使用当前偏好，还是先更新一些内容再搜索？',
  sidebarTutorial: {
    eyebrow: '小提示',
    title: '想给聊天留出更多空间？',
    body: '使用这个图标可以随时收起或重新打开你的 Smart Search 资料面板。',
    dismiss: '知道了',
    savedProfile: {
      eyebrow: '资料面板',
      title: '已保存资料',
      body: '这个按钮会显示 Mimi 为之后 Smart Search 聊天保存的偏好。',
      dismiss: '知道了',
    },
    currentSearch: {
      eyebrow: '资料面板',
      title: '当前搜索',
      body: '用这个按钮查看、对比或保存你这次搜索正在使用的条件。',
      dismiss: '知道了',
    },
  },
  languageSelectorTutorial: {
    eyebrow: '小提示',
    title: '选择你的语言',
    body: '使用地球图标，可以把 Mimi 的引导问题切换成你偏好的语言。',
    dismiss: '知道了',
  },
  mapToggleTutorial: {
    eyebrow: '小提示',
    title: '打开地图',
    body: '点这个地图按钮，就能在地图上查看推荐房源。',
    dismiss: '知道了',
  },
  mapResizeTutorial: {
    eyebrow: '提示',
    title: '拖动角落缩放',
    body: '拉动这个角。',
    dismiss: '知道了',
  },
  mapCommuteModeTutorial: {
    eyebrow: '提示',
    title: '切换出行方式',
    body: '悬停房源可查看通勤。',
    dismiss: '知道了',
  },
  questions: {
    identity: '哪一项最符合你的情况？',
    schoolName: 'Mimi 应该使用哪所学校来帮你搜索？',
    locationMode: 'Mimi 应该如何设置你的地点偏好？',
    location: '告诉我地点，我来确认准确位置。',
    commuteOption: '开始搜索前，要添加一个通勤目的地吗？',
    commuteDestination: '你通常会通勤到哪里？',
    commuteMethod: '你通常会怎样通勤？',
    maxCommuteTime: '你能接受的最长通勤时间是多少？',
    propertyType: '你想找哪种房源类型？',
    budget: '你的预算是多少？请选择或填写价格范围。',
    moveInDate: '你希望什么时候入住？',
    bedrooms: '你需要几间卧室？',
    bathrooms: '你需要几间浴室？',
    parking: '你需要停车位吗？',
    pets: '你有宠物吗？',
    mustHaveInUnit: '房间内部有哪些必需配置？选择不能缺少的项目，或跳过。',
    mustHaveAmenities: '楼内或周边有哪些必需设施？选择重要项目，或跳过。',
    recommendationStyle: 'Mimi 应该如何优先排序第一批推荐？',
  },
  questionPlaceholders: {
    parking: '例如：需要、不需要、最好有',
    pets: '例如：没有宠物、猫、小型犬',
  },
  identityLabels: {
    work: '工作',
    student: '学校 / 学生',
    other: '其他',
  },
  identityChatMessages: {
    work: '我想住得离工作地点近一些。',
    student: '我想住得离学校近一些。',
    other: '我有其他优先考虑。',
  },
  identityCards: {
    work: { label: '靠近工作地点', description: '靠近工作或日常通勤地点' },
    student: { label: '靠近学校', description: '靠近学校、研究地点或校园生活圈' },
    other: { label: '其他需求', description: '我有其他找房原因' },
  },
  locationModeLabels: {
    commute: '寻找附近房源',
    live: '已有目标区域',
    explore: '还不确定',
  },
  locationModeDescriptions: {
    commute: '围绕日常目的地控制通勤时间。',
    live: '我已经有想住的社区或地址。',
    explore: '我刚来波士顿，希望先获得一些建议。',
  },
  locationQuestions: {
    studentCommuteTarget: schoolName => `请选择 ${schoolName} 的通勤目标。你可以选择下方校园建筑，或搜索准确地址 / 地标。`,
    studentCampusTarget: schoolName => `请为 ${schoolName} 选择校园建筑。如果列表中没有，请搜索准确地址或地标。`,
    workCommuteTarget: 'Mimi 应该把哪个工作地点或办公室作为你的通勤目标？',
    otherCommuteTarget: '你通常会通勤到哪里？',
    dailyDestination: 'Mimi 应该把哪个日常目的地作为你的通勤目标？',
    confirmBeforeLivingAreas: '选择居住区域前，请先确认你的通勤目标。',
    addLivingAreas: '添加一个或多个居住区域。选择支持的区域或搜索 Google 地点，然后继续。',
    confirmBeforeBroadSearch: '开始更广泛搜索前，请先确认你的通勤目标。',
    addStarterLivingAreas: '添加一个或多个初始居住区域。第一批结果出来后我们可以继续细化。',
  },
  commuteModeDescription: {
    withTarget: target => `控制到 ${target} 的通勤时间。`,
    withoutTarget: '围绕日常目的地控制通勤时间。',
  },
  postResultCommuteQuestion: '您想为当前搜索添加通勤目的地吗？',
  postResultCommuteAnswerLabels: { yes: '是', no: '否' },
  commuteOptionLabels: {
    skip: '暂时跳过通勤',
    target: '有目标地点',
  },
  commuteOptionDescriptions: {
    skip: '不使用通勤筛选进行搜索。',
    target: '用 Google 选择一个单独的目的地。',
  },
  commuteMethodLabels: {
    driving: '开车',
    transit: '公共交通',
    walking: '步行',
    bicycling: '骑行',
  },
  commuteTimeLabels: {
    ...en.commuteTimeLabels,
    '': '无所谓',
  },
  propertyTypeLabels: {
    apartment: '公寓',
    condo: '共管公寓',
    townhouse: '联排别墅',
    joint_rent: '合租',
    any: '无所谓',
  },
  propertyTypeNoPreference: '无所谓',
  budgetLabels: {
    others: '其他',
    min: '最低',
    max: '最高',
    apply: '确定',
  },
  moveInDateLabels: {
    title: '入住日期',
    selectDate: '选择日期',
    rangeSeparator: '至',
    earliestAriaLabel: '选择最早入住日期',
    latestAriaLabel: '选择最晚入住日期',
    asap: '尽快入住',
    asapAriaLabel: '选择尽快入住',
  },
  bedroomModeLabels: {
    at_least: '至少',
    exact: '精确',
  },
  bedroomLabels: {
    studio: '单身公寓',
    '1': '1 间卧室',
    '2': '2 间卧室',
    '3': '3 间卧室',
  },
  bathroomLabels: {
    '1': '≥ 1 间浴室',
    '2': '≥ 2 间浴室',
    '3': '≥ 3 间浴室',
  },
  parkingAnswerLabels: {
    yes: '需要',
    no: '不需要',
  },
  petAnswerLabels: {
    yes: '有',
    no: '没有',
  },
  recommendationStyles: {
    value: {
      optionLabel: '选项 A',
      title: '性价比优先',
      description: '优先平衡价格和房源配置。',
    },
    commute: {
      optionLabel: '选项 B',
      title: '通勤优先',
      description: '优先考虑便利性和更短通勤时间。',
    },
    balanced: {
      optionLabel: '选项 C',
      title: '综合最佳匹配',
      description: '平衡价格、通勤和生活方式。',
    },
  },
  recommendationStyleDisabledReasons: {
    commuteNoLimit: '你选择通勤时间无所谓，所以 Mimi 暂时不会使用通勤优先排序。',
    commuteMissingTarget: '需要通勤目标和最长时间。Mimi 暂时会跳过这个选项。',
    balancedComingSoon: '综合排序逻辑仍在调校中，即将推出。',
  },
  schoolStep: {
    supportedQuestion: '你的学校是哪一所？',
    supportedHint: '从我们支持的学校列表中选择其他学校。',
    unsupportedSearchButton: '没有看到学校？在这里搜索！',
    backToSchoolList: '返回学校列表',
    checkingCampusBuildings: '正在检查校园建筑...',
    noCampusBuildingsTitle: '暂时没有找到校园建筑',
    noCampusBuildingsBody: '请搜索准确的校园地址、建筑名称或附近地标。',
    searchExactPlace: '搜索准确地点',
    unsupportedBuildingSearchButton: '没有看到建筑？在这里搜索！',
    backToCampusBuildings: '返回校园建筑',
  },
  livingAreaStep: {
    popularAreaQuickPicks: '热门区域快捷选择',
    addSupportedArea: '从支持列表中添加其他区域。',
    searchExactPlace: '搜索准确地址、地标或地点。',
    unsupportedAreaSearchButton: '没有看到区域？在这里搜索！',
    backToSupportedAreas: '返回地区列表',
  },
  priorCommuteStep: {
    explorePrompt: '使用这个作为通勤目的地，也可以编辑或跳过通勤，然后我会进行更广泛搜索。',
    livePrompt: '使用这个作为通勤目的地，也可以编辑或跳过通勤，然后选择居住区域。',
    edit: '编辑',
    cancel: '取消',
    enterAnotherLocation: '输入其他地点',
    skipCommute: '跳过通勤',
    confirm: '确认',
  },
  ui: {
    continue: '继续',
    skip: '跳过',
    noSpecificWorkAddress: '还没有具体地址？',
    confirm: '确认',
    confirmTarget: '确认目标',
    confirmFilter: '确认筛选',
    exclusionProfileSaveNote: "区域排除条件仅适用于本次搜索，不会保存到你的资料中。",
    noMatch: '不匹配',
    match: '匹配方式',
    loadingOptions: '正在加载选项…',
    noMustHaves: '没有必需项',
    selectAllThatApply: '可多选。',
  },
};

const zhTW: SmartSearchGuidedCopy = {
  ...zhCN,
  languageName: '中文（繁體）',
  identityIntro: '你好，我是你的 AI 租屋助手 Mimi。我們先依照你的搬家原因建立搜尋方向，再根據需求與生活方式縮小選項。',
  smartProfileGuidedUpdateIntro: '好的。我們來複查你已儲存的 Smart Search 資料，並更新任何不再適合的內容。',
  returningProfileGreeting: '歡迎回來。我已為這次聊天載入你儲存的 Smart Search 資料。你想繼續使用目前偏好，還是先更新一些內容再搜尋？',
  sidebarTutorial: {
    eyebrow: '小提示',
    title: '想給聊天留出更多空間？',
    body: '使用這個圖示可以隨時收合或重新開啟你的 Smart Search 資料面板。',
    dismiss: '知道了',
    savedProfile: {
      eyebrow: '資料面板',
      title: '已儲存資料',
      body: '這個按鈕會顯示 Mimi 為之後 Smart Search 聊天儲存的偏好。',
      dismiss: '知道了',
    },
    currentSearch: {
      eyebrow: '資料面板',
      title: '目前搜尋',
      body: '用這個按鈕查看、比較或儲存你這次搜尋正在使用的條件。',
      dismiss: '知道了',
    },
  },
  languageSelectorTutorial: {
    eyebrow: '小提示',
    title: '選擇你的語言',
    body: '使用地球圖示，可以把 Mimi 的引導問題切換成你偏好的語言。',
    dismiss: '知道了',
  },
  mapToggleTutorial: {
    eyebrow: '小提示',
    title: '打開地圖',
    body: '點這個地圖按鈕，就能在地圖上查看推薦房源。',
    dismiss: '知道了',
  },
  mapResizeTutorial: {
    eyebrow: '提示',
    title: '拖曳角落縮放',
    body: '拉這個角。',
    dismiss: '知道了',
  },
  mapCommuteModeTutorial: {
    eyebrow: '提示',
    title: '切換出行方式',
    body: '將游標停在房源上可查看通勤。',
    dismiss: '知道了',
  },
  questions: {
    identity: '哪一項最符合你的情況？',
    schoolName: 'Mimi 應該使用哪所學校來幫你搜尋？',
    locationMode: 'Mimi 應該如何設定你的地點偏好？',
    location: '告訴我地點，我來確認正確位置。',
    commuteOption: '開始搜尋前，要新增一個通勤目的地嗎？',
    commuteDestination: '你通常會通勤到哪裡？',
    commuteMethod: '你通常會怎麼通勤？',
    maxCommuteTime: '你能接受的最長通勤時間是多少？',
    propertyType: '你想找哪種房源類型？',
    budget: '你的預算是多少？請選擇或填寫價格範圍。',
    moveInDate: '你希望什麼時候入住？',
    bedrooms: '你需要幾間臥室？',
    bathrooms: '你需要幾間浴室？',
    parking: '你需要停車位嗎？',
    pets: '你有寵物嗎？',
    mustHaveInUnit: '房內有哪些必備配置？選擇不能缺少的項目，或跳過。',
    mustHaveAmenities: '大樓或周邊有哪些必備設施？選擇重要項目，或跳過。',
    recommendationStyle: 'Mimi 應該如何優先排序第一批推薦？',
  },
  questionPlaceholders: {
    parking: '例如：需要、不需要、最好有',
    pets: '例如：沒有寵物、貓、小型犬',
  },
  identityCards: {
    work: { label: '靠近工作地點', description: '靠近工作或日常通勤地點' },
    student: { label: '靠近學校', description: '靠近學校、研究地點或校園生活圈' },
    other: { label: '其他需求', description: '我有其他找房原因' },
  },
  identityLabels: {
    work: '工作',
    student: '學校 / 學生',
    other: '其他',
  },
  identityChatMessages: {
    work: '我想住得離工作地點近一些。',
    student: '我想住得離學校近一些。',
    other: '我有其他優先考量。',
  },
  locationModeLabels: {
    commute: '尋找附近房源',
    live: '已有目標區域',
    explore: '還不確定',
  },
  locationModeDescriptions: {
    commute: '圍繞日常目的地控制通勤時間。',
    live: '我已經有想住的社區或地址。',
    explore: '我剛來波士頓，希望先獲得一些建議。',
  },
  locationQuestions: {
    studentCommuteTarget: schoolName => `請選擇 ${schoolName} 的通勤目標。你可以選擇下方校園建築，或搜尋正確地址 / 地標。`,
    studentCampusTarget: schoolName => `請為 ${schoolName} 選擇校園建築。如果列表中沒有，請搜尋正確地址或地標。`,
    workCommuteTarget: 'Mimi 應該把哪個工作地點或辦公室作為你的通勤目標？',
    otherCommuteTarget: '你通常會通勤到哪裡？',
    dailyDestination: 'Mimi 應該把哪個日常目的地作為你的通勤目標？',
    confirmBeforeLivingAreas: '選擇居住區域前，請先確認你的通勤目標。',
    addLivingAreas: '新增一個或多個居住區域。選擇支援的區域或搜尋 Google 地點，然後繼續。',
    confirmBeforeBroadSearch: '開始更廣泛搜尋前，請先確認你的通勤目標。',
    addStarterLivingAreas: '新增一個或多個初始居住區域。第一批結果出來後我們可以繼續細化。',
  },
  commuteModeDescription: {
    withTarget: target => `控制到 ${target} 的通勤時間。`,
    withoutTarget: '圍繞日常目的地控制通勤時間。',
  },
  postResultCommuteQuestion: '您想為目前的搜尋新增通勤目的地嗎？',
  postResultCommuteAnswerLabels: { yes: '是', no: '否' },
  commuteOptionLabels: {
    skip: '暫時跳過通勤',
    target: '有目標地點',
  },
  commuteOptionDescriptions: {
    skip: '不使用通勤篩選進行搜尋。',
    target: '用 Google 選擇一個單獨的目的地。',
  },
  commuteMethodLabels: {
    driving: '開車',
    transit: '公共交通',
    walking: '步行',
    bicycling: '騎行',
  },
  commuteTimeLabels: {
    ...en.commuteTimeLabels,
    '': '無所謂',
  },
  propertyTypeLabels: {
    apartment: '公寓',
    condo: '共管公寓',
    townhouse: '聯排別墅',
    joint_rent: '合租',
    any: '無所謂',
  },
  propertyTypeNoPreference: '無所謂',
  budgetLabels: {
    others: '其他',
    min: '最低',
    max: '最高',
    apply: '套用',
  },
  moveInDateLabels: {
    title: '入住日期',
    selectDate: '選擇日期',
    rangeSeparator: '至',
    earliestAriaLabel: '選擇最早入住日期',
    latestAriaLabel: '選擇最晚入住日期',
    asap: '儘快入住',
    asapAriaLabel: '選擇儘快入住',
  },
  bedroomModeLabels: {
    at_least: '至少',
    exact: '精確',
  },
  bedroomLabels: {
    studio: '单身公寓',
    '1': '1 間臥室',
    '2': '2 間臥室',
    '3': '3 間臥室',
  },
  bathroomLabels: {
    '1': '≥ 1 間浴室',
    '2': '≥ 2 間浴室',
    '3': '≥ 3 間浴室',
  },
  parkingAnswerLabels: {
    yes: '需要',
    no: '不需要',
  },
  petAnswerLabels: {
    yes: '有',
    no: '沒有',
  },
  recommendationStyles: {
    value: {
      optionLabel: '選項 A',
      title: '性價比優先',
      description: '優先平衡價格和房源配置。',
    },
    commute: {
      optionLabel: '選項 B',
      title: '通勤優先',
      description: '優先考慮便利性和更短通勤時間。',
    },
    balanced: {
      optionLabel: '選項 C',
      title: '綜合最佳匹配',
      description: '平衡價格、通勤和生活方式。',
    },
  },
  recommendationStyleDisabledReasons: {
    commuteNoLimit: '你選擇通勤時間無所謂，所以 Mimi 暫時不會使用通勤優先排序。',
    commuteMissingTarget: '需要通勤目標和最長時間。Mimi 暫時會跳過這個選項。',
    balancedComingSoon: '綜合排序邏輯仍在調校中，即將推出。',
  },
  schoolStep: {
    supportedQuestion: '你的學校是哪一所？',
    supportedHint: '從我們支援的學校列表中選擇其他學校。',
    unsupportedSearchButton: '沒有看到學校？在這裡搜尋！',
    backToSchoolList: '返回學校列表',
    checkingCampusBuildings: '正在檢查校園建築...',
    noCampusBuildingsTitle: '暫時沒有找到校園建築',
    noCampusBuildingsBody: '請搜尋正確的校園地址、建築名稱或附近地標。',
    searchExactPlace: '搜尋正確地點',
    unsupportedBuildingSearchButton: '沒有看到建築？在這裡搜尋！',
    backToCampusBuildings: '返回校園建築',
  },
  livingAreaStep: {
    popularAreaQuickPicks: '熱門區域快捷選擇',
    addSupportedArea: '從支援列表中新增其他區域。',
    searchExactPlace: '搜尋正確地址、地標或地點。',
    unsupportedAreaSearchButton: '沒有看到區域？在這裡搜尋！',
    backToSupportedAreas: '返回地區列表',
  },
  priorCommuteStep: {
    ...zhCN.priorCommuteStep,
    edit: '編輯',
    cancel: '取消',
    enterAnotherLocation: '輸入其他地點',
    skipCommute: '跳過通勤',
    confirm: '確認',
  },
  ui: {
    continue: '繼續',
    skip: '跳過',
    noSpecificWorkAddress: '還沒有具體地址？',
    confirm: '確認',
    confirmTarget: '確認目標',
    confirmFilter: '確認篩選',
    exclusionProfileSaveNote: "區域排除條件僅適用於本次搜尋，不會儲存到你的個人資料中。",
    noMatch: '不匹配',
    match: '匹配方式',
    loadingOptions: '正在載入選項…',
    noMustHaves: '沒有必需項',
    selectAllThatApply: '可多選。',
  },
};

const es: SmartSearchGuidedCopy = {
  ...en,
  languageName: 'Español',
  identityIntro: 'Hola, soy Mimi, tu agente de alquiler con IA. Empecemos por el motivo de tu mudanza y luego reduciré las opciones según tus requisitos y estilo de vida.',
  smartProfileGuidedUpdateIntro: 'Claro. Revisemos tu perfil guardado de Smart Search y actualicemos lo que ya no encaje.',
  returningProfileGreeting: 'Te doy la bienvenida de nuevo. Cargué tu perfil guardado de Smart Search para este chat. ¿Quieres continuar con tus preferencias actuales o actualizar algo antes de buscar?',
  sidebarTutorial: {
    eyebrow: 'Consejo rápido',
    title: '¿Necesitas más espacio para el chat?',
    body: 'Usa este ícono para contraer o volver a abrir tu panel de perfil de Smart Search cuando quieras.',
    dismiss: 'Entendido',
    savedProfile: {
      eyebrow: 'Panel de perfil',
      title: 'Perfil guardado',
      body: 'Este botón muestra las preferencias que Mimi guardó para futuros chats de Smart Search.',
      dismiss: 'Entendido',
    },
    currentSearch: {
      eyebrow: 'Panel de perfil',
      title: 'Búsqueda actual',
      body: 'Usa este botón para comparar o guardar los criterios de la búsqueda en la que estás trabajando.',
      dismiss: 'Entendido',
    },
  },
  languageSelectorTutorial: {
    eyebrow: 'Consejo rápido',
    title: 'Elige tu idioma',
    body: 'Usa el globo para cambiar las preguntas guiadas de Mimi al idioma que prefieras.',
    dismiss: 'Entendido',
  },
  mapToggleTutorial: {
    eyebrow: 'Consejo rápido',
    title: 'Abre el mapa',
    body: 'Usa este botón del mapa para explorar tus viviendas recomendadas en un mapa.',
    dismiss: 'Entendido',
  },
  mapResizeTutorial: {
    eyebrow: 'Consejo',
    title: 'Arrastra la esquina',
    body: 'Tira de esta esquina.',
    dismiss: 'Entendido',
  },
  mapCommuteModeTutorial: {
    eyebrow: 'Consejo',
    title: 'Cambia el modo',
    body: 'Pasa el cursor para ver el trayecto.',
    dismiss: 'Entendido',
  },
  questions: {
    identity: '¿Qué opción te describe mejor?',
    schoolName: '¿Qué escuela debería usar Mimi para tu búsqueda?',
    locationMode: '¿Cómo debería Mimi configurar tu perfil de ubicación?',
    location: 'Dime el lugar para confirmar la ubicación correcta.',
    commuteOption: '¿Quieres agregar un destino de traslado antes de buscar?',
    commuteDestination: '¿A dónde te trasladarías normalmente?',
    commuteMethod: '¿Cómo sueles trasladarte allí?',
    maxCommuteTime: '¿Cuál es el tiempo máximo de traslado que aceptarías?',
    propertyType: '¿Qué tipo de vivienda estás buscando?',
    budget: '¿Cuál es tu presupuesto? Indica el rango de precio.',
    moveInDate: '¿Cuándo te gustaría mudarte?',
    bedrooms: '¿Cuántos dormitorios quieres?',
    bathrooms: '¿Cuántos baños necesitas?',
    parking: '¿Necesitas estacionamiento?',
    pets: '¿Tienes mascotas?',
    mustHaveInUnit: '¿Alguna característica imprescindible dentro de la unidad? Elige lo que no puede faltar o salta este paso.',
    mustHaveAmenities: '¿Alguna amenidad imprescindible del edificio o vecindario? Elige lo importante o salta este paso.',
    recommendationStyle: '¿Cómo debería Mimi priorizar tus primeras recomendaciones?',
  },
  questionPlaceholders: {
    parking: 'p. ej., sí, no, sería ideal',
    pets: 'p. ej., sin mascotas, gato, perro pequeño',
  },
  identityLabels: {
    work: 'Trabajo',
    student: 'Escuela / estudiante',
    other: 'Otro',
  },
  identityChatMessages: {
    work: 'Me gustaría vivir cerca del trabajo.',
    student: 'Me gustaría vivir cerca de la escuela.',
    other: 'Tengo otra prioridad en mente.',
  },
  identityCards: {
    work: { label: 'Cerca del trabajo', description: 'Cerca del trabajo o de tu rutina entre semana' },
    student: { label: 'Cerca de la escuela', description: 'Cerca de la escuela, investigación o vida del campus' },
    other: { label: 'Otro motivo', description: 'Busco por otra razón' },
  },
  locationModeLabels: {
    commute: 'Buscar viviendas cercanas',
    live: 'Tengo una zona en mente',
    explore: 'No lo tengo claro',
  },
  locationModeDescriptions: {
    commute: 'Controlar el tiempo de traslado a un destino diario.',
    live: 'Ya tengo un vecindario o dirección en mente.',
    explore: 'Soy nuevo en Boston y quiero sugerencias.',
  },
  locationQuestions: {
    studentCommuteTarget: schoolName => `Elige tu destino de traslado para ${schoolName}. Selecciona un edificio del campus o busca una dirección o referencia exacta.`,
    studentCampusTarget: schoolName => `Para ${schoolName}, elige tu edificio del campus. Si no aparece, busca la dirección o referencia exacta.`,
    workCommuteTarget: '¿Qué lugar de trabajo u oficina debería usar Mimi como destino de traslado?',
    otherCommuteTarget: '¿A dónde te trasladarías?',
    dailyDestination: '¿Qué destino diario debería usar Mimi como referencia de traslado?',
    confirmBeforeLivingAreas: 'Confirma tu destino de traslado antes de elegir zonas para vivir.',
    addLivingAreas: 'Agrega una o más zonas para vivir. Elige zonas compatibles o busca lugares de Google y continúa.',
    confirmBeforeBroadSearch: 'Confirma tu destino de traslado antes de buscar de forma amplia.',
    addStarterLivingAreas: 'Agrega una o más zonas iniciales. Podemos refinar después de los primeros resultados.',
  },
  commuteModeDescription: {
    withTarget: target => `Controlar el tiempo de traslado a ${target}.`,
    withoutTarget: 'Controlar el tiempo de traslado a un destino diario.',
  },
  postResultCommuteQuestion: '¿Quieres agregar un destino de traslado a tu búsqueda actual?',
  postResultCommuteAnswerLabels: { yes: 'Sí', no: 'No' },
  commuteOptionLabels: {
    skip: 'Omitir traslado por ahora',
    target: 'Tengo un destino',
  },
  commuteOptionDescriptions: {
    skip: 'Buscar sin filtro de traslado.',
    target: 'Elegir un destino separado con Google.',
  },
  commuteMethodLabels: {
    driving: 'Auto',
    transit: 'Transporte público',
    walking: 'A pie',
    bicycling: 'Bicicleta',
  },
  commuteTimeLabels: {
    ...en.commuteTimeLabels,
    '': 'No importa',
  },
  propertyTypeLabels: {
    apartment: 'Apartamento',
    condo: 'Condominio',
    townhouse: 'Casa adosada',
    joint_rent: 'Alquiler compartido',
    any: 'No importa',
  },
  propertyTypeNoPreference: 'No importa',
  budgetLabels: {
    others: 'Otro',
    min: 'Mín.',
    max: 'Máx.',
    apply: 'Aplicar',
  },
  moveInDateLabels: {
    title: 'Fecha de mudanza',
    selectDate: 'Seleccionar fecha',
    rangeSeparator: 'a',
    earliestAriaLabel: 'Seleccionar la fecha de mudanza más temprana',
    latestAriaLabel: 'Seleccionar la fecha de mudanza más tardía',
    asap: 'Lo antes posible',
    asapAriaLabel: 'Mudarse lo antes posible',
  },
  bedroomModeLabels: {
    at_least: 'Al menos',
    exact: 'Exacto',
  },
  bedroomLabels: {
    studio: 'Estudio',
    '1': '1 dormitorio',
    '2': '2 dormitorios',
    '3': '3 dormitorios',
  },
  bathroomLabels: {
    '1': '≥ 1 baño',
    '2': '≥ 2 baños',
    '3': '≥ 3 baños',
  },
  parkingAnswerLabels: {
    yes: 'Sí',
    no: 'No',
  },
  petAnswerLabels: {
    yes: 'Sí',
    no: 'No',
  },
  recommendationStyles: {
    value: { optionLabel: 'Opción A', title: 'Mejor valor', description: 'Prioriza el mejor equilibrio entre precio y características.' },
    commute: { optionLabel: 'Opción B', title: 'Traslado primero', description: 'Prioriza comodidad y tiempos de traslado más cortos.' },
    balanced: { optionLabel: 'Opción C', title: 'Mejor ajuste general', description: 'Equilibra precio, traslado y estilo de vida.' },
  },
  recommendationStyleDisabledReasons: {
    commuteNoLimit: 'Dijiste que el tiempo de traslado no importa, así que Mimi omitirá esta prioridad por ahora.',
    commuteMissingTarget: 'Necesita un destino y tiempo máximo de traslado. Mimi omitirá esto por ahora.',
    balancedComingSoon: 'Próximamente mientras ajustamos la lógica de ranking equilibrado.',
  },
  schoolStep: {
    supportedQuestion: '¿Cuál es tu universidad?',
    supportedHint: 'Elige otra escuela de nuestra lista compatible.',
    unsupportedSearchButton: '¿No ves tu escuela? ¡Busca aquí!',
    backToSchoolList: 'Volver a la lista',
    checkingCampusBuildings: 'Buscando edificios del campus...',
    noCampusBuildingsTitle: 'Aún no se encontraron edificios del campus',
    noCampusBuildingsBody: 'Busca la dirección exacta del campus, nombre del edificio o una referencia cercana.',
    searchExactPlace: 'Buscar lugar exacto',
    unsupportedBuildingSearchButton: '¿No ves el edificio? ¡Busca aquí!',
    backToCampusBuildings: 'Volver a edificios del campus',
  },
  livingAreaStep: {
    popularAreaQuickPicks: 'Zonas populares',
    addSupportedArea: 'Agrega otra zona de nuestra lista compatible.',
    searchExactPlace: 'Busca una dirección, referencia o lugar exacto.',
    unsupportedAreaSearchButton: '¿No ves tu zona? ¡Busca aquí!',
    backToSupportedAreas: 'Volver a zonas compatibles',
  },
  priorCommuteStep: {
    explorePrompt: 'Usa esto como destino de traslado, edítalo u omite el traslado antes de buscar ampliamente.',
    livePrompt: 'Usa esto como destino de traslado, edítalo u omite el traslado antes de elegir dónde vivir.',
    edit: 'Editar',
    cancel: 'Cancelar',
    enterAnotherLocation: 'Ingresar otro lugar',
    skipCommute: 'Omitir traslado',
    confirm: 'Confirmar',
  },
  ui: {
    continue: 'Continuar',
    skip: 'Omitir',
    noSpecificWorkAddress: '¿Aún no tienes una dirección específica?',
    confirm: 'Confirmar',
    confirmTarget: 'Confirmar destino',
    confirmFilter: 'Confirmar filtro',
    exclusionProfileSaveNote: "Las áreas excluidas solo se aplican a esta búsqueda y no se guardan en tu perfil.",
    noMatch: 'Sin coincidencia',
    match: 'Coincidencia',
    loadingOptions: 'Cargando opciones…',
    noMustHaves: 'Sin imprescindibles',
    selectAllThatApply: 'Selecciona todas las que correspondan.',
  },
};

const ko: SmartSearchGuidedCopy = {
  ...en,
  languageName: '한국어',
  identityIntro: '안녕하세요, AI 렌털 에이전트 Mimi입니다. 이사 이유를 기준으로 검색 방향을 잡고, 조건과 라이프스타일에 맞춰 선택지를 좁혀드릴게요.',
  smartProfileGuidedUpdateIntro: '좋아요. 저장된 Smart Search 프로필을 검토하고 더 이상 맞지 않는 내용을 업데이트해 볼게요.',
  returningProfileGreeting: '다시 오신 것을 환영합니다. 이 채팅에 저장된 Smart Search 프로필을 불러왔습니다. 현재 선호도를 계속 사용할까요, 아니면 검색 전에 업데이트할까요?',
  sidebarTutorial: {
    eyebrow: '빠른 팁',
    title: '채팅 공간이 더 필요하신가요?',
    body: '이 아이콘으로 Smart Search 프로필 패널을 언제든 접거나 다시 열 수 있어요.',
    dismiss: '알겠어요',
    savedProfile: {
      eyebrow: '프로필 패널',
      title: '저장된 프로필',
      body: '이 버튼은 Mimi가 이후 Smart Search 채팅을 위해 저장한 선호도를 보여줍니다.',
      dismiss: '알겠어요',
    },
    currentSearch: {
      eyebrow: '프로필 패널',
      title: '현재 검색',
      body: '이 버튼으로 지금 진행 중인 검색 조건을 비교하거나 저장할 수 있어요.',
      dismiss: '알겠어요',
    },
  },
  languageSelectorTutorial: {
    eyebrow: '빠른 팁',
    title: '언어를 선택하세요',
    body: '지구본 아이콘으로 Mimi의 안내 질문을 원하는 언어로 바꿀 수 있어요.',
    dismiss: '알겠어요',
  },
  mapToggleTutorial: {
    eyebrow: '빠른 팁',
    title: '지도 열기',
    body: '이 지도 버튼으로 추천 매물을 지도에서 확인할 수 있어요.',
    dismiss: '알겠어요',
  },
  mapResizeTutorial: {
    eyebrow: '팁',
    title: '모서리를 끌어 조절',
    body: '이 모서리를 당기세요.',
    dismiss: '알겠어요',
  },
  mapCommuteModeTutorial: {
    eyebrow: '팁',
    title: '이동 방식 바꾸기',
    body: '집에 올려두면 출퇴근이 보여요.',
    dismiss: '알겠어요',
  },
  questions: {
    identity: '어떤 상황에 가장 가까우신가요?',
    schoolName: 'Mimi가 검색에 사용할 학교는 어디인가요?',
    locationMode: 'Mimi가 위치 프로필을 어떻게 설정하면 좋을까요?',
    location: '정확한 위치를 확인할 수 있도록 장소를 알려주세요.',
    commuteOption: '검색 전에 통근 목적지를 추가할까요?',
    commuteDestination: '보통 어디로 통근하시나요?',
    commuteMethod: '보통 어떤 방식으로 이동하시나요?',
    maxCommuteTime: '허용 가능한 최대 통근 시간은 얼마인가요?',
    propertyType: '어떤 주거 유형을 찾고 있나요?',
    budget: '예산은 얼마인가요? 가격 범위를 지정해 주세요.',
    moveInDate: '언제 입주하고 싶으신가요?',
    bedrooms: '침실은 몇 개를 원하시나요?',
    bathrooms: '욕실은 몇 개가 필요하신가요?',
    parking: '주차가 필요하신가요?',
    pets: '반려동물이 있나요?',
    mustHaveInUnit: '세대 내부에 꼭 필요한 기능이 있나요? 꼭 필요한 항목을 선택하거나 건너뛰세요.',
    mustHaveAmenities: '건물 또는 동네에서 꼭 필요한 편의시설이 있나요? 중요한 항목을 선택하거나 건너뛰세요.',
    recommendationStyle: 'Mimi가 첫 추천을 어떤 기준으로 우선순위화할까요?',
  },
  questionPlaceholders: {
    parking: '예: 필요, 불필요, 있으면 좋음',
    pets: '예: 반려동물 없음, 고양이, 소형견',
  },
  identityLabels: {
    work: '직장',
    student: '학교 / 학생',
    other: '기타',
  },
  identityChatMessages: {
    work: '직장 근처에 살고 싶어요.',
    student: '학교 근처에 살고 싶어요.',
    other: '다른 우선순위가 있어요.',
  },
  identityCards: {
    work: { label: '직장 근처', description: '직장이나 평일 생활권과 가까운 곳' },
    student: { label: '학교 근처', description: '학교, 연구실 또는 캠퍼스 생활권과 가까운 곳' },
    other: { label: '다른 이유', description: '다른 이유로 찾고 있어요' },
  },
  locationModeLabels: {
    commute: '근처 주택 찾기',
    live: '원하는 지역이 있음',
    explore: '아직 잘 모르겠음',
  },
  locationModeDescriptions: {
    commute: '일상 목적지까지의 통근 시간을 관리합니다.',
    live: '이미 원하는 동네나 주소가 있습니다.',
    explore: '보스턴이 처음이라 추천을 받고 싶습니다.',
  },
  locationQuestions: {
    studentCommuteTarget: schoolName => `${schoolName}의 통근 목적지를 선택하세요. 아래 캠퍼스 건물을 선택하거나 정확한 주소 또는 랜드마크를 검색하세요.`,
    studentCampusTarget: schoolName => `${schoolName}의 캠퍼스 건물을 선택하세요. 목록에 없으면 정확한 주소나 랜드마크를 검색하세요.`,
    workCommuteTarget: 'Mimi가 어떤 직장이나 사무실을 통근 목적지로 사용할까요?',
    otherCommuteTarget: '어디로 통근하시나요?',
    dailyDestination: 'Mimi가 어떤 일상 목적지를 통근 기준으로 사용할까요?',
    confirmBeforeLivingAreas: '거주 지역을 선택하기 전에 통근 목적지를 확인하세요.',
    addLivingAreas: '하나 이상의 거주 지역을 추가하세요. 지원 지역을 선택하거나 Google 장소를 검색한 뒤 계속하세요.',
    confirmBeforeBroadSearch: '넓게 검색하기 전에 통근 목적지를 확인하세요.',
    addStarterLivingAreas: '시작 거주 지역을 하나 이상 추가하세요. 첫 결과 후 더 좁혀갈 수 있습니다.',
  },
  commuteModeDescription: {
    withTarget: target => `${target}까지의 통근 시간을 관리합니다.`,
    withoutTarget: '일상 목적지까지의 통근 시간을 관리합니다.',
  },
  postResultCommuteQuestion: '현재 검색에 통근 목적지를 추가하시겠어요?',
  postResultCommuteAnswerLabels: { yes: '예', no: '아니요' },
  commuteOptionLabels: {
    skip: '지금은 통근 건너뛰기',
    target: '목적지 있음',
  },
  commuteOptionDescriptions: {
    skip: '통근 필터 없이 검색합니다.',
    target: 'Google로 별도 목적지를 선택합니다.',
  },
  commuteMethodLabels: {
    driving: '운전',
    transit: '대중교통',
    walking: '도보',
    bicycling: '자전거',
  },
  commuteTimeLabels: { ...en.commuteTimeLabels, '': '상관없음' },
  propertyTypeLabels: {
    apartment: '아파트',
    condo: '콘도미니엄',
    townhouse: '타운하우스',
    joint_rent: '공동 임차',
    any: '상관없음',
  },
  propertyTypeNoPreference: '상관없음',
  budgetLabels: { others: '기타', min: '최소', max: '최대', apply: '적용' },
  moveInDateLabels: {
    title: '입주 날짜',
    selectDate: '날짜 선택',
    rangeSeparator: '~',
    earliestAriaLabel: '가장 빠른 입주 날짜 선택',
    latestAriaLabel: '가장 늦은 입주 날짜 선택',
    asap: '가능한 한 빨리',
    asapAriaLabel: '가능한 한 빨리 입주',
  },
  bedroomModeLabels: { at_least: '최소', exact: '정확히' },
  bedroomLabels: { studio: '1K', '1': '침실 1개', '2': '침실 2개', '3': '침실 3개' },
  bathroomLabels: { '1': '≥ 욕실 1개', '2': '≥ 욕실 2개', '3': '≥ 욕실 3개' },
  parkingAnswerLabels: { yes: '예', no: '아니요' },
  petAnswerLabels: { yes: '예', no: '아니요' },
  recommendationStyles: {
    value: { optionLabel: '옵션 A', title: '가성비 우선', description: '가격과 기능의 균형을 우선합니다.' },
    commute: { optionLabel: '옵션 B', title: '통근 우선', description: '편리함과 더 짧은 이동 시간을 우선합니다.' },
    balanced: { optionLabel: '옵션 C', title: '종합 최적 매칭', description: '가격, 통근, 라이프스타일을 균형 있게 봅니다.' },
  },
  recommendationStyleDisabledReasons: {
    commuteNoLimit: '통근 시간이 상관없다고 하셨으므로 Mimi가 통근 우선 순위를 지금은 건너뜁니다.',
    commuteMissingTarget: '통근 목적지와 최대 시간이 필요합니다. Mimi가 지금은 건너뜁니다.',
    balancedComingSoon: '균형형 랭킹 로직을 조정 중이며 곧 제공됩니다.',
  },
  schoolStep: {
    supportedQuestion: '대학교가 어디인가요?',
    supportedHint: '지원 목록에서 다른 학교를 선택하세요.',
    unsupportedSearchButton: '학교가 없나요? 여기서 검색하세요!',
    backToSchoolList: '학교 목록으로 돌아가기',
    checkingCampusBuildings: '캠퍼스 건물 확인 중...',
    noCampusBuildingsTitle: '아직 캠퍼스 건물을 찾지 못했습니다',
    noCampusBuildingsBody: '정확한 캠퍼스 주소, 건물명 또는 근처 랜드마크를 검색하세요.',
    searchExactPlace: '정확한 장소 검색',
    unsupportedBuildingSearchButton: '건물이 없나요? 여기서 검색하세요!',
    backToCampusBuildings: '캠퍼스 건물로 돌아가기',
  },
  livingAreaStep: {
    popularAreaQuickPicks: '인기 지역 빠른 선택',
    addSupportedArea: '지원 목록에서 다른 지역을 추가하세요.',
    searchExactPlace: '정확한 주소, 랜드마크 또는 장소를 검색하세요.',
    unsupportedAreaSearchButton: '지역이 없나요? 여기서 검색하세요!',
    backToSupportedAreas: '지원 지역 목록으로 돌아가기',
  },
  priorCommuteStep: {
    explorePrompt: '이를 통근 목적지로 사용하거나 수정하거나, 넓게 검색하기 전에 통근을 건너뛰세요.',
    livePrompt: '이를 통근 목적지로 사용하거나 수정하거나, 거주 지역 선택 전에 통근을 건너뛰세요.',
    edit: '수정',
    cancel: '취소',
    enterAnotherLocation: '다른 위치 입력',
    skipCommute: '통근 건너뛰기',
    confirm: '확인',
  },
  ui: {
    continue: '계속',
    skip: '건너뛰기',
    noSpecificWorkAddress: '아직 정확한 주소가 없나요?',
    confirm: '확인',
    confirmTarget: '목적지 확인',
    confirmFilter: '필터 확인',
    exclusionProfileSaveNote: "제외 지역은 이번 검색에만 적용되며 프로필에 저장되지 않습니다.",
    noMatch: '일치 없음',
    match: '매칭',
    loadingOptions: '옵션 로딩 중…',
    noMustHaves: '필수 조건 없음',
    selectAllThatApply: '해당하는 항목을 모두 선택할 수 있습니다.',
  },
};

const ja: SmartSearchGuidedCopy = {
  ...en,
  languageName: '日本語',
  identityIntro: 'こんにちは、AI賃貸エージェントのMimiです。まず引っ越し理由を軸に検索条件を整え、そのあと希望条件とライフスタイルに合わせて候補を絞ります。',
  smartProfileGuidedUpdateIntro: '承知しました。保存済みのSmart Searchプロフィールを確認し、今の希望に合わない内容を更新しましょう。',
  returningProfileGreeting: 'おかえりなさい。このチャット用に保存済みのSmart Searchプロフィールを読み込みました。現在の希望条件で続けますか、それとも検索前に更新しますか？',
  sidebarTutorial: {
    eyebrow: 'クイックヒント',
    title: 'チャットをもっと広く使いたいですか？',
    body: 'このアイコンでSmart Searchプロフィールパネルをいつでも折りたたんだり開き直したりできます。',
    dismiss: 'わかりました',
    savedProfile: {
      eyebrow: 'プロフィールパネル',
      title: '保存済みプロフィール',
      body: 'このボタンでは、今後のSmart Searchチャット用にMimiが保存した希望条件を確認できます。',
      dismiss: 'わかりました',
    },
    currentSearch: {
      eyebrow: 'プロフィールパネル',
      title: '現在の検索',
      body: 'このボタンで、今進めている検索条件を比較したり保存したりできます。',
      dismiss: 'わかりました',
    },
  },
  languageSelectorTutorial: {
    eyebrow: 'クイックヒント',
    title: '言語を選びましょう',
    body: '地球アイコンから、Mimiのガイド質問を希望する言語に切り替えられます。',
    dismiss: 'わかりました',
  },
  mapToggleTutorial: {
    eyebrow: 'クイックヒント',
    title: '地図を開く',
    body: 'この地図ボタンで、おすすめ物件を地図上で確認できます。',
    dismiss: 'わかりました',
  },
  mapResizeTutorial: {
    eyebrow: 'ヒント',
    title: '角をドラッグ',
    body: 'この角を引いてください。',
    dismiss: 'わかりました',
  },
  mapCommuteModeTutorial: {
    eyebrow: 'ヒント',
    title: '移動手段を切替',
    body: '物件に重ねると通勤が見えます。',
    dismiss: 'わかりました',
  },
  questions: {
    identity: 'どれが一番近いですか？',
    schoolName: 'Mimiはどの学校を検索に使えばよいですか？',
    locationMode: 'Mimiは位置条件をどのように設定すればよいですか？',
    location: '正しい場所を確認するため、地点を教えてください。',
    commuteOption: '検索前に通勤先を追加しますか？',
    commuteDestination: '普段どこへ通勤・通学しますか？',
    commuteMethod: '通常どの方法で移動しますか？',
    maxCommuteTime: '許容できる最長の移動時間はどれくらいですか？',
    propertyType: 'どの物件タイプを探していますか？',
    budget: '予算はいくらですか？価格帯を指定してください。',
    moveInDate: 'いつ入居したいですか？',
    bedrooms: '寝室はいくつ必要ですか？',
    bathrooms: 'バスルームはいくつ必要ですか？',
    parking: '駐車場は必要ですか？',
    pets: 'ペットはいますか？',
    mustHaveInUnit: '室内で必須の設備はありますか？欠かせないものを選ぶかスキップしてください。',
    mustHaveAmenities: '建物や周辺で必須の設備はありますか？重要なものを選ぶかスキップしてください。',
    recommendationStyle: 'Mimiは最初のおすすめをどう優先すればよいですか？',
  },
  questionPlaceholders: {
    parking: '例：必要、不要、あれば嬉しい',
    pets: '例：ペットなし、猫、小型犬',
  },
  identityLabels: { work: '仕事', student: '学校 / 学生', other: 'その他' },
  identityChatMessages: {
    work: '職場の近くに住みたいです。',
    student: '学校の近くに住みたいです。',
    other: '別の優先条件があります。',
  },
  identityCards: {
    work: { label: '職場の近く', description: '職場や平日の生活圏に近い場所' },
    student: { label: '学校の近く', description: '学校、研究施設、キャンパス生活に近い場所' },
    other: { label: 'その他', description: '別の理由で探しています' },
  },
  locationModeLabels: {
    commute: '近くの住宅を探す',
    live: '住みたいエリアがある',
    explore: 'まだ決まっていない',
  },
  locationModeDescriptions: {
    commute: '日常的な目的地までの移動時間を重視します。',
    live: 'すでに希望のエリアや住所があります。',
    explore: 'ボストンに詳しくないので提案がほしいです。',
  },
  locationQuestions: {
    studentCommuteTarget: schoolName => `${schoolName} の通学先を選んでください。下のキャンパス建物を選ぶか、正確な住所またはランドマークを検索してください。`,
    studentCampusTarget: schoolName => `${schoolName} のキャンパス建物を選んでください。見つからない場合は正確な住所またはランドマークを検索してください。`,
    workCommuteTarget: 'Mimiはどの職場またはオフィスを通勤先として使えばよいですか？',
    otherCommuteTarget: 'どこへ通勤しますか？',
    dailyDestination: 'Mimiはどの日常的な目的地を通勤先として使えばよいですか？',
    confirmBeforeLivingAreas: '住みたいエリアを選ぶ前に通勤先を確認してください。',
    addLivingAreas: '住みたいエリアを1つ以上追加してください。対応エリアを選ぶかGoogleの場所を検索して続けます。',
    confirmBeforeBroadSearch: '広く検索する前に通勤先を確認してください。',
    addStarterLivingAreas: '最初の候補エリアを1つ以上追加してください。最初の結果後に絞り込めます。',
  },
  commuteModeDescription: {
    withTarget: target => `${target} までの移動時間を重視します。`,
    withoutTarget: '日常的な目的地までの移動時間を重視します。',
  },
  postResultCommuteQuestion: '現在の検索に通勤先を追加しますか？',
  postResultCommuteAnswerLabels: { yes: 'はい', no: 'いいえ' },
  commuteOptionLabels: { skip: '今は通勤条件をスキップ', target: '目的地がある' },
  commuteOptionDescriptions: {
    skip: '通勤フィルターなしで検索します。',
    target: 'Googleで別の目的地を選びます。',
  },
  commuteMethodLabels: { driving: '車', transit: '公共交通', walking: '徒歩', bicycling: '自転車' },
  commuteTimeLabels: { ...en.commuteTimeLabels, '': 'こだわらない' },
  propertyTypeLabels: {
    apartment: 'アパート',
    condo: 'コンドミニアム',
    townhouse: 'タウンハウス',
    joint_rent: '共同賃借',
    any: 'こだわらない',
  },
  propertyTypeNoPreference: 'こだわらない',
  budgetLabels: { others: 'その他', min: '最小', max: '最大', apply: '適用' },
  moveInDateLabels: {
    title: '入居日',
    selectDate: '日付を選択',
    rangeSeparator: '〜',
    earliestAriaLabel: '最も早い入居日を選択',
    latestAriaLabel: '最も遅い入居日を選択',
    asap: 'できるだけ早く',
    asapAriaLabel: 'できるだけ早く入居',
  },
  bedroomModeLabels: { at_least: '以上', exact: 'ちょうど' },
  bedroomLabels: { studio: '1R', '1': '1寝室', '2': '2寝室', '3': '3寝室' },
  bathroomLabels: { '1': '≥ 1バスルーム', '2': '≥ 2バスルーム', '3': '≥ 3バスルーム' },
  parkingAnswerLabels: { yes: 'はい', no: 'いいえ' },
  petAnswerLabels: { yes: 'はい', no: 'いいえ' },
  recommendationStyles: {
    value: { optionLabel: '選択肢A', title: 'コスパ重視', description: '価格と設備のバランスを優先します。' },
    commute: { optionLabel: '選択肢B', title: '通勤重視', description: '利便性と短い移動時間を優先します。' },
    balanced: { optionLabel: '選択肢C', title: '総合ベストマッチ', description: '価格、通勤、ライフスタイルをバランスよく見ます。' },
  },
  recommendationStyleDisabledReasons: {
    commuteNoLimit: '移動時間はこだわらないとのことなので、Mimiは通勤重視の並び替えを今はスキップします。',
    commuteMissingTarget: '通勤先と最大時間が必要です。Mimiは今はスキップします。',
    balancedComingSoon: 'バランス型ランキングを調整中です。近日対応予定です。',
  },
  schoolStep: {
    supportedQuestion: '大学はどこですか？',
    supportedHint: '対応リストから別の学校を選んでください。',
    unsupportedSearchButton: '学校が見つかりませんか？ここで検索！',
    backToSchoolList: '学校リストに戻る',
    checkingCampusBuildings: 'キャンパス建物を確認中...',
    noCampusBuildingsTitle: 'キャンパス建物がまだ見つかりません',
    noCampusBuildingsBody: '正確なキャンパス住所、建物名、または近くのランドマークを検索してください。',
    searchExactPlace: '正確な場所を検索',
    unsupportedBuildingSearchButton: '建物が見つかりませんか？ここで検索！',
    backToCampusBuildings: 'キャンパス建物に戻る',
  },
  livingAreaStep: {
    popularAreaQuickPicks: '人気エリア',
    addSupportedArea: '対応リストから別のエリアを追加してください。',
    searchExactPlace: '正確な住所、ランドマーク、場所を検索してください。',
    unsupportedAreaSearchButton: 'エリアが見つかりませんか？ここで検索！',
    backToSupportedAreas: '対応エリアリストに戻る',
  },
  priorCommuteStep: {
    explorePrompt: 'これを通勤先として使うか、編集するか、広く検索する前に通勤条件をスキップしてください。',
    livePrompt: 'これを通勤先として使うか、編集するか、住むエリアを選ぶ前に通勤条件をスキップしてください。',
    edit: '編集',
    cancel: 'キャンセル',
    enterAnotherLocation: '別の場所を入力',
    skipCommute: '通勤をスキップ',
    confirm: '確認',
  },
  ui: {
    continue: '続ける',
    skip: 'スキップ',
    noSpecificWorkAddress: 'まだ具体的な住所が決まっていませんか？',
    confirm: '確認',
    confirmTarget: '目的地を確認',
    confirmFilter: 'フィルターを確認',
    exclusionProfileSaveNote: "除外エリアは今回の検索にのみ適用され、プロフィールには保存されません。",
    noMatch: '一致なし',
    match: '条件',
    loadingOptions: '選択肢を読み込み中…',
    noMustHaves: '必須条件なし',
    selectAllThatApply: '該当する項目を複数選べます。',
  },
};

const vi: SmartSearchGuidedCopy = {
  ...en,
  languageName: 'Tiếng Việt',
  identityIntro: 'Xin chào, mình là Mimi, trợ lý thuê nhà AI của bạn. Hãy bắt đầu từ lý do bạn chuyển nhà, rồi mình sẽ thu hẹp lựa chọn theo nhu cầu và lối sống của bạn.',
  smartProfileGuidedUpdateIntro: 'Được thôi. Hãy xem lại hồ sơ Smart Search đã lưu và cập nhật những phần không còn phù hợp.',
  returningProfileGreeting: 'Chào mừng bạn quay lại. Mình đã tải hồ sơ Smart Search đã lưu cho cuộc trò chuyện này. Bạn muốn tiếp tục với các ưu tiên hiện tại hay cập nhật trước khi tìm kiếm?',
  sidebarTutorial: {
    eyebrow: 'Mẹo nhanh',
    title: 'Bạn cần thêm không gian cho khung chat?',
    body: 'Dùng biểu tượng này để thu gọn hoặc mở lại bảng hồ sơ Smart Search bất cứ lúc nào.',
    dismiss: 'Đã hiểu',
    savedProfile: {
      eyebrow: 'Bảng hồ sơ',
      title: 'Hồ sơ đã lưu',
      body: 'Nút này hiển thị các ưu tiên Mimi đã lưu cho những cuộc trò chuyện Smart Search sau này.',
      dismiss: 'Đã hiểu',
    },
    currentSearch: {
      eyebrow: 'Bảng hồ sơ',
      title: 'Tìm kiếm hiện tại',
      body: 'Dùng nút này để so sánh hoặc lưu các tiêu chí của lượt tìm kiếm bạn đang thực hiện.',
      dismiss: 'Đã hiểu',
    },
  },
  languageSelectorTutorial: {
    eyebrow: 'Mẹo nhanh',
    title: 'Chọn ngôn ngữ của bạn',
    body: 'Dùng biểu tượng quả địa cầu để chuyển các câu hỏi hướng dẫn của Mimi sang ngôn ngữ bạn muốn.',
    dismiss: 'Đã hiểu',
  },
  mapToggleTutorial: {
    eyebrow: 'Mẹo nhanh',
    title: 'Mở bản đồ',
    body: 'Dùng nút bản đồ này để xem các căn hộ được gợi ý trên bản đồ.',
    dismiss: 'Đã hiểu',
  },
  mapResizeTutorial: {
    eyebrow: 'Mẹo',
    title: 'Kéo góc này',
    body: 'Kéo góc này.',
    dismiss: 'Đã hiểu',
  },
  mapCommuteModeTutorial: {
    eyebrow: 'Mẹo',
    title: 'Đổi cách di chuyển',
    body: 'Di chuột để xem lộ trình.',
    dismiss: 'Đã hiểu',
  },
  questions: {
    identity: 'Điều nào mô tả bạn đúng nhất?',
    schoolName: 'Mimi nên dùng trường nào cho tìm kiếm của bạn?',
    locationMode: 'Mimi nên thiết lập hồ sơ vị trí của bạn như thế nào?',
    location: 'Cho mình biết địa điểm để xác nhận vị trí chính xác.',
    commuteOption: 'Bạn có muốn thêm điểm đến đi lại trước khi tìm kiếm không?',
    commuteDestination: 'Bạn thường đi đến đâu?',
    commuteMethod: 'Bạn thường di chuyển đến đó bằng cách nào?',
    maxCommuteTime: 'Thời gian di chuyển tối đa bạn chấp nhận là bao lâu?',
    propertyType: 'Bạn đang tìm loại nhà nào?',
    budget: 'Ngân sách của bạn là bao nhiêu? Vui lòng chọn hoặc nhập khoảng giá.',
    moveInDate: 'Bạn muốn chuyển vào khi nào?',
    bedrooms: 'Bạn muốn mấy phòng ngủ?',
    bathrooms: 'Bạn cần mấy phòng tắm?',
    parking: 'Bạn có cần chỗ đậu xe không?',
    pets: 'Bạn có thú cưng không?',
    mustHaveInUnit: 'Có tiện nghi nào trong căn hộ bắt buộc phải có không? Chọn mục cần thiết hoặc bỏ qua.',
    mustHaveAmenities: 'Có tiện ích tòa nhà hoặc khu vực nào bắt buộc không? Chọn mục quan trọng hoặc bỏ qua.',
    recommendationStyle: 'Mimi nên ưu tiên các gợi ý đầu tiên như thế nào?',
  },
  questionPlaceholders: {
    parking: 'ví dụ: có, không, có thì tốt',
    pets: 'ví dụ: không thú cưng, mèo, chó nhỏ',
  },
  identityLabels: { work: 'Công việc', student: 'Trường / sinh viên', other: 'Khác' },
  identityChatMessages: {
    work: 'Tôi muốn sống gần nơi làm việc.',
    student: 'Tôi muốn sống gần trường.',
    other: 'Tôi có ưu tiên khác.',
  },
  identityCards: {
    work: { label: 'Gần nơi làm việc', description: 'Gần công việc hoặc lịch sinh hoạt trong tuần' },
    student: { label: 'Gần trường học', description: 'Gần trường, nơi nghiên cứu hoặc đời sống campus' },
    other: { label: 'Lý do khác', description: 'Tôi tìm nhà vì lý do khác' },
  },
  locationModeLabels: {
    commute: 'Tìm nhà ở gần',
    live: 'Đã có khu vực mong muốn',
    explore: 'Chưa chắc chắn',
  },
  locationModeDescriptions: {
    commute: 'Kiểm soát thời gian di chuyển đến điểm đến hằng ngày.',
    live: 'Tôi đã có khu phố hoặc địa chỉ muốn sống.',
    explore: 'Tôi mới đến Boston và muốn được gợi ý.',
  },
  locationQuestions: {
    studentCommuteTarget: schoolName => `Chọn điểm đến đi lại cho ${schoolName}. Chọn một tòa nhà trong campus bên dưới hoặc tìm địa chỉ / địa danh chính xác.`,
    studentCampusTarget: schoolName => `Với ${schoolName}, hãy chọn tòa nhà trong campus. Nếu không có trong danh sách, hãy tìm địa chỉ hoặc địa danh chính xác.`,
    workCommuteTarget: 'Mimi nên dùng nơi làm việc hoặc văn phòng nào làm điểm đến đi lại của bạn?',
    otherCommuteTarget: 'Bạn sẽ đi đến đâu?',
    dailyDestination: 'Mimi nên dùng điểm đến hằng ngày nào làm điểm đến đi lại của bạn?',
    confirmBeforeLivingAreas: 'Hãy xác nhận điểm đến đi lại trước khi chọn khu vực sống.',
    addLivingAreas: 'Thêm một hoặc nhiều khu vực sống. Chọn khu vực được hỗ trợ hoặc tìm địa điểm Google, rồi tiếp tục.',
    confirmBeforeBroadSearch: 'Hãy xác nhận điểm đến đi lại trước khi mình tìm kiếm rộng hơn.',
    addStarterLivingAreas: 'Thêm một hoặc nhiều khu vực sống ban đầu. Chúng ta có thể tinh chỉnh sau khi có kết quả đầu tiên.',
  },
  commuteModeDescription: {
    withTarget: target => `Kiểm soát thời gian di chuyển đến ${target}.`,
    withoutTarget: 'Kiểm soát thời gian di chuyển đến điểm đến hằng ngày.',
  },
  postResultCommuteQuestion: 'Bạn có muốn thêm điểm đến đi lại vào tìm kiếm hiện tại không?',
  postResultCommuteAnswerLabels: { yes: 'Có', no: 'Không' },
  commuteOptionLabels: {
    skip: 'Tạm bỏ qua đi lại',
    target: 'Có điểm đến',
  },
  commuteOptionDescriptions: {
    skip: 'Tìm kiếm không dùng bộ lọc đi lại.',
    target: 'Chọn một điểm đến riêng bằng Google.',
  },
  commuteMethodLabels: { driving: 'Lái xe', transit: 'Phương tiện công cộng', walking: 'Đi bộ', bicycling: 'Đạp xe' },
  commuteTimeLabels: { ...en.commuteTimeLabels, '': 'Không quan trọng' },
  propertyTypeLabels: {
    apartment: 'Căn hộ',
    condo: 'Căn hộ chung cư',
    townhouse: 'Nhà liền kề',
    joint_rent: 'Thuê chung',
    any: 'Không quan trọng',
  },
  propertyTypeNoPreference: 'Không quan trọng',
  budgetLabels: { others: 'Khác', min: 'Tối thiểu', max: 'Tối đa', apply: 'Áp dụng' },
  moveInDateLabels: {
    title: 'Ngày chuyển vào',
    selectDate: 'Chọn ngày',
    rangeSeparator: 'đến',
    earliestAriaLabel: 'Chọn ngày chuyển vào sớm nhất',
    latestAriaLabel: 'Chọn ngày chuyển vào muộn nhất',
    asap: 'Sớm nhất có thể',
    asapAriaLabel: 'Chuyển vào sớm nhất có thể',
  },
  bedroomModeLabels: { at_least: 'Ít nhất', exact: 'Chính xác' },
  bedroomLabels: { studio: 'Căn hộ studio', '1': '1 phòng ngủ', '2': '2 phòng ngủ', '3': '3 phòng ngủ' },
  bathroomLabels: { '1': '≥ 1 phòng tắm', '2': '≥ 2 phòng tắm', '3': '≥ 3 phòng tắm' },
  parkingAnswerLabels: { yes: 'Có', no: 'Không' },
  petAnswerLabels: { yes: 'Có', no: 'Không' },
  recommendationStyles: {
    value: { optionLabel: 'Lựa chọn A', title: 'Ưu tiên giá trị', description: 'Ưu tiên cân bằng tốt nhất giữa giá và tiện ích.' },
    commute: { optionLabel: 'Lựa chọn B', title: 'Ưu tiên đi lại', description: 'Ưu tiên sự thuận tiện và thời gian di chuyển ngắn hơn.' },
    balanced: { optionLabel: 'Lựa chọn C', title: 'Phù hợp tổng thể nhất', description: 'Cân bằng giá, đi lại và lối sống.' },
  },
  recommendationStyleDisabledReasons: {
    commuteNoLimit: 'Bạn nói thời gian đi lại không quan trọng, nên Mimi sẽ tạm bỏ qua xếp hạng ưu tiên đi lại.',
    commuteMissingTarget: 'Cần có điểm đến và thời gian đi lại tối đa. Mimi sẽ tạm bỏ qua mục này.',
    balancedComingSoon: 'Sắp ra mắt trong lúc chúng tôi tinh chỉnh logic xếp hạng cân bằng.',
  },
  schoolStep: {
    supportedQuestion: 'Trường đại học của bạn là gì?',
    supportedHint: 'Chọn một trường khác từ danh sách được hỗ trợ.',
    unsupportedSearchButton: 'Không thấy trường của bạn? Tìm tại đây!',
    backToSchoolList: 'Quay lại danh sách trường',
    checkingCampusBuildings: 'Đang kiểm tra các tòa nhà trong campus...',
    noCampusBuildingsTitle: 'Chưa tìm thấy tòa nhà trong campus',
    noCampusBuildingsBody: 'Tìm địa chỉ campus chính xác, tên tòa nhà hoặc địa danh gần đó.',
    searchExactPlace: 'Tìm địa điểm chính xác',
    unsupportedBuildingSearchButton: 'Không thấy tòa nhà? Tìm tại đây!',
    backToCampusBuildings: 'Quay lại các tòa nhà trong campus',
  },
  livingAreaStep: {
    popularAreaQuickPicks: 'Khu vực phổ biến',
    addSupportedArea: 'Thêm khu vực khác từ danh sách được hỗ trợ.',
    searchExactPlace: 'Tìm địa chỉ, địa danh hoặc địa điểm chính xác.',
    unsupportedAreaSearchButton: 'Không thấy khu vực của bạn? Tìm tại đây!',
    backToSupportedAreas: 'Quay lại danh sách khu vực được hỗ trợ',
  },
  priorCommuteStep: {
    explorePrompt: 'Dùng nơi này làm điểm đến đi lại, chỉnh sửa, hoặc bỏ qua đi lại trước khi mình tìm kiếm rộng hơn.',
    livePrompt: 'Dùng nơi này làm điểm đến đi lại, chỉnh sửa, hoặc bỏ qua đi lại trước khi chọn khu vực sống.',
    edit: 'Sửa',
    cancel: 'Hủy',
    enterAnotherLocation: 'Nhập địa điểm khác',
    skipCommute: 'Bỏ qua đi lại',
    confirm: 'Xác nhận',
  },
  ui: {
    continue: 'Tiếp tục',
    skip: 'Bỏ qua',
    noSpecificWorkAddress: 'Chưa có địa chỉ cụ thể?',
    confirm: 'Xác nhận',
    confirmTarget: 'Xác nhận điểm đến',
    confirmFilter: 'Xác nhận bộ lọc',
    exclusionProfileSaveNote: "Các khu vực bị loại trừ chỉ áp dụng cho tìm kiếm này và không được lưu vào hồ sơ của bạn.",
    noMatch: 'Không khớp',
    match: 'Khớp',
    loadingOptions: 'Đang tải lựa chọn…',
    noMustHaves: 'Không có mục bắt buộc',
    selectAllThatApply: 'Bạn có thể chọn nhiều mục phù hợp.',
  },
};

const fr: SmartSearchGuidedCopy = {
  ...en,
  languageName: 'Français',
  identityIntro: 'Bonjour, je suis Mimi, votre agente de location IA. Commençons par la raison de votre déménagement, puis je réduirai les options selon vos critères et votre mode de vie.',
  smartProfileGuidedUpdateIntro: 'Bien sûr. Passons en revue votre profil Smart Search enregistré et mettons à jour ce qui ne convient plus.',
  returningProfileGreeting: 'Bon retour. J’ai chargé votre profil Smart Search enregistré pour cette conversation. Voulez-vous continuer avec vos préférences actuelles ou les modifier avant la recherche ?',
  sidebarTutorial: {
    eyebrow: 'Astuce rapide',
    title: 'Besoin de plus d’espace pour le chat ?',
    body: 'Utilisez cette icône pour réduire ou rouvrir votre panneau de profil Smart Search à tout moment.',
    dismiss: 'Compris',
    savedProfile: {
      eyebrow: 'Panneau de profil',
      title: 'Profil enregistré',
      body: 'Ce bouton affiche les préférences que Mimi a enregistrées pour vos prochains chats Smart Search.',
      dismiss: 'Compris',
    },
    currentSearch: {
      eyebrow: 'Panneau de profil',
      title: 'Recherche actuelle',
      body: 'Utilisez ce bouton pour comparer ou enregistrer les critères de la recherche en cours.',
      dismiss: 'Compris',
    },
  },
  languageSelectorTutorial: {
    eyebrow: 'Astuce rapide',
    title: 'Choisissez votre langue',
    body: 'Utilisez le globe pour passer les questions guidées de Mimi dans la langue de votre choix.',
    dismiss: 'Compris',
  },
  mapToggleTutorial: {
    eyebrow: 'Astuce rapide',
    title: 'Ouvrir la carte',
    body: 'Utilisez ce bouton carte pour explorer vos logements recommandés sur une carte.',
    dismiss: 'Compris',
  },
  mapResizeTutorial: {
    eyebrow: 'Astuce',
    title: 'Glissez le coin',
    body: 'Tirez ce coin.',
    dismiss: 'Compris',
  },
  mapCommuteModeTutorial: {
    eyebrow: 'Astuce',
    title: 'Changez le mode',
    body: 'Survolez un logement pour voir le trajet.',
    dismiss: 'Compris',
  },
  questions: {
    identity: 'Quelle option vous décrit le mieux ?',
    schoolName: 'Quelle école Mimi doit-elle utiliser pour votre recherche ?',
    locationMode: 'Comment Mimi doit-elle configurer votre profil de localisation ?',
    location: 'Indiquez-moi le lieu afin que je confirme la bonne localisation.',
    commuteOption: 'Voulez-vous ajouter une destination de trajet avant la recherche ?',
    commuteDestination: 'Où iriez-vous habituellement ?',
    commuteMethod: 'Comment vous y rendez-vous habituellement ?',
    maxCommuteTime: 'Quel est le temps de trajet maximum que vous accepteriez ?',
    propertyType: 'Quel type de logement recherchez-vous ?',
    budget: 'Quel est votre budget ? Veuillez préciser la fourchette de prix.',
    moveInDate: 'Quand souhaitez-vous emménager ?',
    bedrooms: 'Combien de chambres voulez-vous ?',
    bathrooms: 'Combien de salles de bain vous faut-il ?',
    parking: 'Avez-vous besoin d’un stationnement ?',
    pets: 'Avez-vous des animaux ?',
    mustHaveInUnit: 'Des équipements indispensables dans le logement ? Choisissez ce qui est essentiel ou passez.',
    mustHaveAmenities: 'Des commodités indispensables dans l’immeuble ou le quartier ? Choisissez ce qui compte ou passez.',
    recommendationStyle: 'Comment Mimi doit-elle prioriser vos premières recommandations ?',
  },
  questionPlaceholders: {
    parking: 'ex. oui, non, idéalement',
    pets: 'ex. pas d’animaux, chat, petit chien',
  },
  identityLabels: { work: 'Travail', student: 'École / étudiant', other: 'Autre' },
  identityChatMessages: {
    work: 'Je voudrais vivre près du travail.',
    student: 'Je voudrais vivre près de l’école.',
    other: 'J’ai une autre priorité en tête.',
  },
  identityCards: {
    work: { label: 'Près du travail', description: 'Proche du travail ou de votre routine en semaine' },
    student: { label: 'Près de l’école', description: 'Proche de l’école, du labo ou de la vie de campus' },
    other: { label: 'Autre besoin', description: 'Je cherche pour une autre raison' },
  },
  locationModeLabels: {
    commute: 'Trouver des logements à proximité',
    live: 'J’ai une zone en tête',
    explore: 'Pas encore sûr',
  },
  locationModeDescriptions: {
    commute: 'Contrôler le temps de trajet vers une destination quotidienne.',
    live: 'J’ai déjà un quartier ou une adresse en tête.',
    explore: 'Je découvre Boston et j’aimerais des suggestions.',
  },
  locationQuestions: {
    studentCommuteTarget: schoolName => `Choisissez votre destination de trajet pour ${schoolName}. Sélectionnez un bâtiment du campus ci-dessous ou recherchez une adresse ou un repère exact.`,
    studentCampusTarget: schoolName => `Pour ${schoolName}, choisissez votre bâtiment du campus. S’il n’est pas listé, recherchez l’adresse ou le repère exact.`,
    workCommuteTarget: 'Quel lieu de travail ou bureau Mimi doit-elle utiliser comme destination de trajet ?',
    otherCommuteTarget: 'Où vous rendriez-vous habituellement ?',
    dailyDestination: 'Quelle destination quotidienne Mimi doit-elle utiliser comme destination de trajet ?',
    confirmBeforeLivingAreas: 'Confirmez votre destination de trajet avant de choisir les zones de vie.',
    addLivingAreas: 'Ajoutez une ou plusieurs zones de vie. Choisissez des zones prises en charge ou recherchez des lieux Google, puis continuez.',
    confirmBeforeBroadSearch: 'Confirmez votre destination de trajet avant que je recherche plus largement.',
    addStarterLivingAreas: 'Ajoutez une ou plusieurs zones de départ. Nous pourrons affiner après les premiers résultats.',
  },
  commuteModeDescription: {
    withTarget: target => `Contrôler le temps de trajet vers ${target}.`,
    withoutTarget: 'Contrôler le temps de trajet vers une destination quotidienne.',
  },
  postResultCommuteQuestion: 'Souhaitez-vous ajouter une destination de trajet à votre recherche actuelle ?',
  postResultCommuteAnswerLabels: { yes: 'Oui', no: 'Non' },
  commuteOptionLabels: {
    skip: 'Passer le trajet pour le moment',
    target: 'J’ai une destination',
  },
  commuteOptionDescriptions: {
    skip: 'Rechercher sans filtre de trajet.',
    target: 'Choisir une destination séparée avec Google.',
  },
  commuteMethodLabels: {
    driving: 'Voiture',
    transit: 'Transports en commun',
    walking: 'Marche',
    bicycling: 'Vélo',
  },
  commuteTimeLabels: { ...en.commuteTimeLabels, '': 'Peu importe' },
  propertyTypeLabels: {
    apartment: 'Appartement',
    condo: 'Condo',
    townhouse: 'Maison de ville',
    joint_rent: 'Colocation',
    any: 'Peu importe',
  },
  propertyTypeNoPreference: 'Peu importe',
  budgetLabels: { others: 'Autre', min: 'Min.', max: 'Max.', apply: 'Appliquer' },
  moveInDateLabels: {
    title: 'Date d’emménagement',
    selectDate: 'Choisir une date',
    rangeSeparator: 'à',
    earliestAriaLabel: 'Choisir la date d’emménagement la plus tôt',
    latestAriaLabel: 'Choisir la date d’emménagement la plus tard',
    asap: 'Dès que possible',
    asapAriaLabel: 'Emménager dès que possible',
  },
  bedroomModeLabels: { at_least: 'Au moins', exact: 'Exact' },
  bedroomLabels: { studio: 'Studio', '1': '1 chambre', '2': '2 chambres', '3': '3 chambres' },
  bathroomLabels: { '1': '≥ 1 salle de bain', '2': '≥ 2 salles de bain', '3': '≥ 3 salles de bain' },
  parkingAnswerLabels: { yes: 'Oui', no: 'Non' },
  petAnswerLabels: { yes: 'Oui', no: 'Non' },
  recommendationStyles: {
    value: { optionLabel: 'Option A', title: 'Meilleur rapport qualité-prix', description: 'Priorise le meilleur équilibre entre prix et caractéristiques.' },
    commute: { optionLabel: 'Option B', title: 'Trajet prioritaire', description: 'Priorise la commodité et les trajets plus courts.' },
    balanced: { optionLabel: 'Option C', title: 'Meilleure correspondance globale', description: 'Équilibre budget, trajet et mode de vie.' },
  },
  recommendationStyleDisabledReasons: {
    commuteNoLimit: 'Vous avez indiqué que le temps de trajet importe peu, donc Mimi ignore ce classement pour le moment.',
    commuteMissingTarget: 'Une destination et un temps maximum sont nécessaires. Mimi ignore cette option pour le moment.',
    balancedComingSoon: 'Bientôt disponible pendant que nous ajustons le classement équilibré.',
  },
  schoolStep: {
    supportedQuestion: 'Quelle est votre université ?',
    supportedHint: 'Choisissez une autre école dans notre liste prise en charge.',
    unsupportedSearchButton: 'Vous ne voyez pas votre école ? Recherchez ici !',
    backToSchoolList: 'Retour à la liste des écoles',
    checkingCampusBuildings: 'Vérification des bâtiments du campus...',
    noCampusBuildingsTitle: 'Aucun bâtiment de campus trouvé pour le moment',
    noCampusBuildingsBody: 'Recherchez l’adresse exacte du campus, le nom du bâtiment ou un repère à proximité.',
    searchExactPlace: 'Rechercher un lieu exact',
    unsupportedBuildingSearchButton: 'Vous ne voyez pas le bâtiment ? Recherchez ici !',
    backToCampusBuildings: 'Retour aux bâtiments du campus',
  },
  livingAreaStep: {
    popularAreaQuickPicks: 'Zones populaires',
    addSupportedArea: 'Ajoutez une autre zone depuis notre liste prise en charge.',
    searchExactPlace: 'Recherchez une adresse, un repère ou un lieu exact.',
    unsupportedAreaSearchButton: 'Vous ne voyez pas votre zone ? Recherchez ici !',
    backToSupportedAreas: 'Retour à la liste des zones prises en charge',
  },
  priorCommuteStep: {
    explorePrompt: 'Utilisez ceci comme destination de trajet, modifiez-la ou passez le trajet avant ma recherche élargie.',
    livePrompt: 'Utilisez ceci comme destination de trajet, modifiez-la ou passez le trajet avant de choisir votre zone de vie.',
    edit: 'Modifier',
    cancel: 'Annuler',
    enterAnotherLocation: 'Saisir un autre lieu',
    skipCommute: 'Passer le trajet',
    confirm: 'Confirmer',
  },
  ui: {
    continue: 'Continuer',
    skip: 'Passer',
    noSpecificWorkAddress: 'Vous n’avez pas encore d’adresse précise ?',
    confirm: 'Confirmer',
    confirmTarget: 'Confirmer la destination',
    confirmFilter: 'Confirmer le filtre',
    exclusionProfileSaveNote: "Les zones exclues s’appliquent uniquement à cette recherche et ne sont pas enregistrées dans votre profil.",
    noMatch: 'Aucune correspondance',
    match: 'Correspondance',
    loadingOptions: 'Chargement des options…',
    noMustHaves: 'Aucun indispensable',
    selectAllThatApply: 'Sélectionnez toutes les options pertinentes.',
  },
};

const arSA: SmartSearchGuidedCopy = {
  ...en,
  languageName: 'العربية (السعودية)',
  textDirection: 'rtl',
  identityIntro: 'مرحبًا، أنا Mimi، وكيلتك الذكية للبحث عن السكن. لنبدأ بسبب انتقالك، ثم أضيّق الخيارات بناءً على متطلباتك ونمط حياتك.',
  smartProfileGuidedUpdateIntro: 'بالتأكيد. لنراجع ملف Smart Search المحفوظ ونحدّث ما لم يعد مناسبًا.',
  returningProfileGreeting: 'مرحبًا بعودتك. حمّلت ملف Smart Search المحفوظ لهذه المحادثة. هل تريد المتابعة بتفضيلاتك الحالية أم تحديث شيء قبل البحث؟',
  sidebarTutorial: {
    eyebrow: 'نصيحة سريعة',
    title: 'هل تحتاج إلى مساحة أكبر للدردشة؟',
    body: 'استخدم هذا الرمز لطي لوحة ملف Smart Search أو فتحها مجددًا في أي وقت.',
    dismiss: 'فهمت',
    savedProfile: {
      eyebrow: 'لوحة الملف',
      title: 'الملف المحفوظ',
      body: 'يعرض هذا الزر التفضيلات التي حفظتها Mimi لمحادثات Smart Search القادمة.',
      dismiss: 'فهمت',
    },
    currentSearch: {
      eyebrow: 'لوحة الملف',
      title: 'البحث الحالي',
      body: 'استخدم هذا الزر لمقارنة أو حفظ معايير البحث الذي تعمل عليه الآن.',
      dismiss: 'فهمت',
    },
  },
  languageSelectorTutorial: {
    eyebrow: 'نصيحة سريعة',
    title: 'اختر لغتك',
    body: 'استخدم رمز الكرة الأرضية لتبديل أسئلة Mimi الإرشادية إلى اللغة التي تفضّلها.',
    dismiss: 'فهمت',
  },
  mapToggleTutorial: {
    eyebrow: 'نصيحة سريعة',
    title: 'افتح الخريطة',
    body: 'استخدم زر الخريطة هذا لاستكشاف المنازل الموصى بها على الخريطة.',
    dismiss: 'فهمت',
  },
  mapResizeTutorial: {
    eyebrow: 'نصيحة',
    title: 'اسحب لتغيير الحجم',
    body: 'اسحب هذه الزاوية.',
    dismiss: 'فهمت',
  },
  mapCommuteModeTutorial: {
    eyebrow: 'نصيحة',
    title: 'غيّر وسيلة التنقل',
    body: 'مرّر على منزل لرؤية التنقل.',
    dismiss: 'فهمت',
  },
  questions: {
    identity: 'ما الخيار الذي يصفك بشكل أفضل؟',
    schoolName: 'أي جامعة يجب أن تستخدمها Mimi في بحثك؟',
    locationMode: 'كيف يجب أن تضبط Mimi تفضيلات موقعك؟',
    location: 'أخبرني بالمكان حتى أؤكد الموقع الصحيح.',
    commuteOption: 'هل تريد إضافة وجهة تنقل قبل البحث؟',
    commuteDestination: 'إلى أين تتنقل عادة؟',
    commuteMethod: 'كيف ستتنقل عادة إلى هناك؟',
    maxCommuteTime: 'ما أطول مدة تنقل يمكنك قبولها؟',
    propertyType: 'ما نوع العقار الذي تبحث عنه؟',
    budget: 'ما ميزانيتك؟ يرجى تحديد نطاق السعر.',
    moveInDate: 'متى ترغب في الانتقال؟',
    bedrooms: 'كم عدد غرف النوم التي تريدها؟',
    bathrooms: 'كم عدد دورات المياه التي تحتاجها؟',
    parking: 'هل تحتاج إلى موقف سيارة؟',
    pets: 'هل لديك حيوانات أليفة؟',
    mustHaveInUnit: 'هل توجد ميزات ضرورية داخل الوحدة؟ اختر ما لا يمكنك الاستغناء عنه أو تخطَّ هذه الخطوة.',
    mustHaveAmenities: 'هل توجد مرافق ضرورية في المبنى أو الحي؟ اختر ما يهمك أو تخطَّ هذه الخطوة.',
    recommendationStyle: 'كيف يجب أن ترتب Mimi أول التوصيات حسب الأولوية؟',
  },
  questionPlaceholders: {
    parking: 'مثال: نعم، لا، يفضل توفره',
    pets: 'مثال: لا يوجد، قطة، كلب صغير',
  },
  identityLabels: {
    work: 'العمل',
    student: 'الجامعة / طالب',
    other: 'أخرى',
  },
  identityChatMessages: {
    work: 'أرغب في السكن بالقرب من العمل.',
    student: 'أرغب في السكن بالقرب من الجامعة.',
    other: 'لدي أولوية مختلفة.',
  },
  identityCards: {
    work: { label: 'بالقرب من العمل', description: 'قريب من العمل أو روتين أيام الأسبوع' },
    student: { label: 'بالقرب من الجامعة', description: 'قريب من الجامعة أو البحث أو حياة الحرم الجامعي' },
    other: { label: 'سبب آخر', description: 'أبحث عن سكن لسبب مختلف' },
  },
  locationModeLabels: {
    commute: 'ابحث عن منازل قريبة',
    live: 'لدي منطقة محددة',
    explore: 'لست متأكدًا بعد',
  },
  locationModeDescriptions: {
    commute: 'التحكم في وقت التنقل إلى وجهة يومية.',
    live: 'لدي حي أو عنوان محدد في ذهني.',
    explore: 'أنا جديد في بوسطن وأرغب في اقتراحات.',
  },
  locationQuestions: {
    studentCommuteTarget: schoolName => `اختر وجهة التنقل لـ ${schoolName}. اختر مبنى في الحرم أدناه أو ابحث عن عنوان أو معلم محدد.`,
    studentCampusTarget: schoolName => `بالنسبة إلى ${schoolName}، اختر مبنى الحرم أدناه. إذا لم يكن مدرجًا، فابحث عن العنوان أو المعلم المحدد.`,
    workCommuteTarget: 'أي مكان عمل أو مكتب يجب أن تستخدمه Mimi كوجهة تنقل؟',
    otherCommuteTarget: 'إلى أين ستتنقل؟',
    dailyDestination: 'أي وجهة يومية يجب أن تستخدمها Mimi كوجهة تنقل؟',
    confirmBeforeLivingAreas: 'أكد وجهة التنقل قبل اختيار مناطق السكن.',
    addLivingAreas: 'أضف منطقة سكن واحدة أو أكثر. اختر مناطق مدعومة أو ابحث في أماكن Google ثم تابع.',
    confirmBeforeBroadSearch: 'أكد وجهة التنقل قبل أن أبحث بشكل أوسع.',
    addStarterLivingAreas: 'أضف منطقة سكن مبدئية واحدة أو أكثر. يمكننا تحسين البحث بعد النتائج الأولى.',
  },
  commuteModeDescription: {
    withTarget: target => `التحكم في وقت التنقل إلى ${target}.`,
    withoutTarget: 'التحكم في وقت التنقل إلى وجهة يومية.',
  },
  postResultCommuteQuestion: 'هل تريد إضافة وجهة تنقل إلى بحثك الحالي؟',
  postResultCommuteAnswerLabels: { yes: 'نعم', no: 'لا' },
  commuteOptionLabels: {
    skip: 'تخطي التنقل الآن',
    target: 'لدي وجهة',
  },
  commuteOptionDescriptions: {
    skip: 'البحث بدون فلتر للتنقل.',
    target: 'اختيار وجهة منفصلة عبر Google.',
  },
  commuteMethodLabels: {
    driving: 'سيارة',
    transit: 'مواصلات عامة',
    walking: 'مشيًا',
    bicycling: 'دراجة هوائية',
  },
  commuteTimeLabels: { ...en.commuteTimeLabels, '': 'لا يهم' },
  propertyTypeLabels: {
    apartment: 'شقة',
    condo: 'كوندو',
    townhouse: 'تاون هاوس',
    joint_rent: 'إيجار مشترك',
    any: 'لا يهم',
  },
  propertyTypeNoPreference: 'لا يهم',
  budgetLabels: { others: 'أخرى', min: 'الحد الأدنى', max: 'الحد الأعلى', apply: 'تطبيق' },
  moveInDateLabels: {
    title: 'تاريخ الانتقال',
    selectDate: 'اختر التاريخ',
    rangeSeparator: 'إلى',
    earliestAriaLabel: 'اختر أبكر تاريخ انتقال',
    latestAriaLabel: 'اختر آخر تاريخ انتقال',
    asap: 'في أقرب وقت ممكن',
    asapAriaLabel: 'الانتقال في أقرب وقت ممكن',
  },
  bedroomModeLabels: { at_least: 'على الأقل', exact: 'بالضبط' },
  bedroomLabels: { studio: 'استوديو', '1': 'غرفة نوم واحدة', '2': 'غرفتا نوم', '3': '3 غرف نوم' },
  bathroomLabels: { '1': '≥ حمام واحد', '2': '≥ حمامان', '3': '≥ 3 حمامات' },
  parkingAnswerLabels: { yes: 'نعم', no: 'لا' },
  petAnswerLabels: { yes: 'نعم', no: 'لا' },
  recommendationStyles: {
    value: { optionLabel: 'الخيار A', title: 'الأفضل قيمة', description: 'يعطي الأولوية لأفضل توازن بين السعر والمميزات.' },
    commute: { optionLabel: 'الخيار B', title: 'الأولوية للتنقل', description: 'يعطي الأولوية للراحة وأوقات التنقل الأقصر.' },
    balanced: { optionLabel: 'الخيار C', title: 'أفضل تطابق عام', description: 'يوازن بين السعر والتنقل ونمط الحياة.' },
  },
  recommendationStyleDisabledReasons: {
    commuteNoLimit: 'ذكرت أن وقت التنقل لا يهم، لذلك ستتخطى Mimi ترتيب التنقل الآن.',
    commuteMissingTarget: 'يلزم وجود وجهة تنقل ووقت أقصى. ستتخطى Mimi هذا الآن.',
    balancedComingSoon: 'قريبًا بينما نضبط منطق الترتيب المتوازن.',
  },
  schoolStep: {
    supportedQuestion: 'ما جامعتك؟',
    supportedHint: 'اختر جامعة أخرى من قائمتنا المدعومة.',
    unsupportedSearchButton: 'لا ترى جامعتك؟ ابحث هنا!',
    backToSchoolList: 'العودة إلى قائمة الجامعات',
    checkingCampusBuildings: 'جارٍ فحص مباني الحرم...',
    noCampusBuildingsTitle: 'لم يتم العثور على مباني حرم بعد',
    noCampusBuildingsBody: 'ابحث عن عنوان الحرم الدقيق أو اسم المبنى أو معلم قريب.',
    searchExactPlace: 'البحث عن مكان محدد',
    unsupportedBuildingSearchButton: 'لا ترى المبنى؟ ابحث هنا!',
    backToCampusBuildings: 'العودة إلى مباني الحرم',
  },
  livingAreaStep: {
    popularAreaQuickPicks: 'اختيارات سريعة لمناطق شائعة',
    addSupportedArea: 'أضف منطقة أخرى من قائمتنا المدعومة.',
    searchExactPlace: 'ابحث عن عنوان أو معلم أو مكان محدد.',
    unsupportedAreaSearchButton: 'لا ترى منطقتك؟ ابحث هنا!',
    backToSupportedAreas: 'العودة إلى قائمة المناطق المدعومة',
  },
  priorCommuteStep: {
    explorePrompt: 'استخدم هذا كوجهة تنقل، أو عدّله، أو تخطَّ التنقل قبل البحث بشكل أوسع.',
    livePrompt: 'استخدم هذا كوجهة تنقل، أو عدّله، أو تخطَّ التنقل قبل اختيار منطقة السكن.',
    edit: 'تعديل',
    cancel: 'إلغاء',
    enterAnotherLocation: 'إدخال موقع آخر',
    skipCommute: 'تخطي التنقل',
    confirm: 'تأكيد',
  },
  ui: {
    continue: 'متابعة',
    skip: 'تخطي',
    noSpecificWorkAddress: 'ليس لديك عنوان محدد بعد؟',
    confirm: 'تأكيد',
    confirmTarget: 'تأكيد الوجهة',
    confirmFilter: 'تأكيد الفلتر',
    exclusionProfileSaveNote: "المناطق المستبعدة تنطبق على هذا البحث فقط ولا تُحفظ في ملفك الشخصي.",
    noMatch: 'لا يوجد تطابق',
    match: 'مطابقة',
    loadingOptions: 'جارٍ تحميل الخيارات…',
    noMustHaves: 'لا توجد ضروريات',
    selectAllThatApply: 'يمكنك اختيار أكثر من خيار يناسبك.',
  },
};

const GUIDED_COPY_BY_LANGUAGE: Record<SmartSearchGuidedLanguageCode, SmartSearchGuidedCopy> = {
  en,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  es,
  ko,
  ja,
  vi,
  fr,
  'ar-SA': arSA,
};

export const getSmartSearchGuidedCopy = (
  language: SmartSearchGuidedLanguageCode = DEFAULT_GUIDED_LANGUAGE
): SmartSearchGuidedCopy => GUIDED_COPY_BY_LANGUAGE[language] || en;
